/* aw-guided-flow.js (variant C "Guided Flow")
 * Step engine + plan card. Economics and cart mechanics IDENTICAL to variant
 * B's aw-order-widget.js. New: the 4-step flow, region derived from the gate
 * ZIP, sessionStorage resume, and the "AW Theme Variant: E" cart attribute
 * (order-attribution marker; this build writes E2).
 */
/* ---------------------------------------------------------------------------
 * Cart transport (2026-08-07). Brian, mobile: "when I click add to cart, it
 * sometimes says something went wrong."
 *
 * ROOT CAUSE, measured on E8 at 393x852: READ AMPLIFICATION on /cart.js. Not a
 * bad variant, not a double submit. ONE tap of "Get my first delivery" put ~15
 * /cart.js requests on the wire, several in the SAME millisecond, and Shopify's
 * edge answered the losers with HTTP 429 and a Cloudflare "Verifying your
 * connection..." HTML body. Attribution of 31 reads captured across two runs:
 *     LB Selleasy      (lb-selleasy.js)                        8
 *     Appstle Bundles  (...volume-discount-custom-attribute)   6
 *     applyOffer()                                             4   (3 of them 429)
 *     aw-cart-sync reconcile()                                 3   (1 of them 429)
 *     doAdd() guard read                                       2
 *     syncExtrasFromCart() -> readCart()                       2
 * The two apps are not ours, but WE summon them: every cartChanged() dispatched
 * 'cart:build', theme.js repainted the drawer, and both apps re-read the cart on
 * that repaint. aw-cart-sync's MutationObserver fires on the same repaint and
 * could dispatch a SECOND 'cart:build', doubling the burst.
 *
 * WHY IT REACHED THE CUSTOMER AS A GENERIC ERROR: doAdd()'s guard read was 3
 * tries 1200ms apart. A rate-limit window is far longer than 3.6s, so all three
 * tries landed inside the same block, the read returned null, doAdd threw
 * 'cart unreadable' and printed "Something went wrong adding to your cart."
 * The retries never rescued the request - they SPENT more of the budget that
 * caused it.
 *
 * THE FIX, in order of effect:
 *  1. ONE SHARED READER. Concurrent callers join a single in-flight request and
 *     a short cache, so three call sites in the same tick cost one round trip.
 *  2. ONE WRITE QUEUE. Every cart write is serialised, so two of our writes are
 *     never in flight together and a read never overlaps our own write.
 *  3. HONEST 429 HANDLING. 429, 5xx, a dropped connection, or an HTML body (what
 *     the edge actually returns) is classified TRANSIENT and retried ONCE with
 *     backoff plus jitter, instead of three times inside the same block.
 *  4. NON-IDEMPOTENT WRITES ARE NEVER BLIND-RETRIED. /cart/add.js is relative,
 *     so a lost response is ambiguous: Shopify may have applied it before the
 *     edge ate the reply. Re-sending would double the order, which this store
 *     has already done once (2026-07-19). addItems() settles, RE-READS, and
 *     re-sends only if the cart proves the add did not land.
 *  5. COALESCED ANNOUNCEMENTS. cartChanged() now dispatches at most one
 *     'cart:build' per settle window, which is what actually throttles the apps.
 *
 * Nothing here changes what is added or what it costs. Every quantity, variant,
 * selling plan and price still comes from awzQuote() and the same item list.
 * A copy of this factory lives in aw-cart-sync.js's consumer shim; the singleton
 * on window.__awCartNet means whichever file loads first owns the queue and the
 * other shares it. Keep the two in sync.
 * ------------------------------------------------------------------------- */
const AWCART = (function () {
  if (window.__awCartNet) return window.__awCartNet;

  const CACHE_MS = 700;          // a burst of callers in one tick shares one read
  const BACKOFF_MS = 1400;       // longer than the theme's old 900/1200 on purpose
  const JITTER_MS = 600;         // de-synchronises two tabs / two modules retrying

  let cached = null;
  let cachedAt = 0;
  let inflight = null;
  let queue = Promise.resolve();

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  function isTransientStatus(s) {
    return s === 429 || s === 0 || (s >= 500 && s < 600);
  }

  /* /cart.js is served as text/javascript, so a content-type check for json
   * reports every SUCCESSFUL read as blocked (documented trap). The reliable
   * tell for an edge block is an HTML body. */
  async function raw(url, init) {
    let status = 0;
    let body = null;
    let netError = null;
    try {
      const res = await fetch(url, init);
      status = res.status;
      body = await res.text();
    } catch (e) {
      netError = String((e && e.message) || e);
    }
    const html = body != null && body.trim().charAt(0) === '<';
    let data = null;
    if (!html && body) { try { data = JSON.parse(body); } catch (_) {} }
    const ok = !netError && status >= 200 && status < 300 && !html;
    return {
      ok: ok,
      status: status,
      data: data,
      html: html,
      netError: netError,
      transient: !!netError || html || isTransientStatus(status),
    };
  }

  /* Idempotent calls only. /cart.js, /cart/change.js (absolute quantity) and
   * /cart/update.js (attributes / discount) can all be safely repeated. */
  async function withOneRetry(url, init) {
    const first = await raw(url, init);
    if (first.ok || !first.transient) return first;
    await sleep(BACKOFF_MS + Math.floor(Math.random() * JITTER_MS));
    const second = await raw(url, init);
    second.retried = true;
    return second;
  }

  function invalidate() { cached = null; cachedAt = 0; }

  function read(opts) {
    const fresh = !!(opts && opts.fresh);
    if (!fresh && cached && Date.now() - cachedAt < CACHE_MS) return Promise.resolve(cached);
    if (inflight) return inflight;                       // join, never duplicate
    inflight = withOneRetry('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        inflight = null;
        if (r.ok && r.data) { cached = r.data; cachedAt = Date.now(); return r.data; }
        const err = new Error('cart read failed');
        err.status = r.status;
        err.transient = r.transient;
        throw err;
      }, function (e) { inflight = null; throw e; });
    return inflight;
  }

  // Never throws: callers that only want a best-effort snapshot use this.
  function readSoft(opts) { return read(opts).catch(function () { return null; }); }

  function post(url, payload, opts) {
    const retry = !(opts && opts.retry === false);
    const run = queue.then(async function () {
      const init = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
      };
      const r = retry ? await withOneRetry(url, init) : await raw(url, init);
      invalidate();                                     // any write invalidates
      return r;
    });
    // The queue has to survive a rejection or every later write is dead.
    queue = run.then(function () {}, function () {});
    return run;
  }

  function qtyOf(cart, variantId) {
    if (!cart || !cart.items) return 0;
    let n = 0;
    for (const it of cart.items) if (Number(it.variant_id) === Number(variantId)) n += it.quantity;
    return n;
  }

  /* The only non-idempotent call in the flow. A transient failure here is
   * AMBIGUOUS, so we look before we leap: settle, re-read, and re-send only if
   * the cart proves the add did not land. `before` is the snapshot taken just
   * before the attempt; without one we fall back to a presence test. */
  async function addItems(items, before) {
    const first = await post('/cart/add.js', { items: items }, { retry: false });
    if (first.ok || !first.transient) return first;

    await sleep(BACKOFF_MS + Math.floor(Math.random() * JITTER_MS));
    let after = null;
    try { after = await read({ fresh: true }); } catch (_) { after = null; }
    if (!after) return first;                            // still blind: report honestly

    const landed = items.every(function (it) {
      const want = Number(it.quantity) || 1;
      const now = qtyOf(after, it.id);
      return before ? now >= qtyOf(before, it.id) + want : now >= want;
    });
    if (landed) return { ok: true, status: 200, data: after, recovered: 'already-applied' };

    const second = await post('/cart/add.js', { items: items }, { retry: false });
    second.retried = true;
    return second;
  }

  /* Coalesced 'cart:build'. The two installed apps re-read /cart.js on every one
   * of these, so firing three in a burst tripled their traffic. Trailing edge:
   * the last write in a burst is the one whose state gets announced. */
  let announceTimer = null;
  function announce() {
    clearTimeout(announceTimer);
    announceTimer = setTimeout(function () {
      announceTimer = null;
      invalidate();
      try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (_) {}
    }, 450);
  }

  /* One place that turns a failed cart call into words a customer can act on.
   * "Something went wrong" told them nothing and, worse, read the same whether
   * the cart was rate limited (retry works) or the variant was rejected (retry
   * never works). */
  function message(result, verb) {
    // An unexpected JS error is not a busy cart: do not tell the customer to
    // retry something that will fail again the same way.
    if (result && result.unknown) {
      return 'We could not complete that. Nothing has been charged. Please reload the page and try again.';
    }
    const transient = !result || result.transient;
    if (transient) {
      return 'Your cart is busy right now. Nothing has been charged. Wait a few seconds and tap ' + verb + ' again.';
    }
    if (result.status === 422) {
      return 'That plan is not available for your area right now. Nothing has been charged. Please pick a different delivery frequency, or contact us and we will set it up for you.';
    }
    return 'We could not ' + (verb === 'Add' ? 'add that to' : 'update') + ' your cart. Nothing has been charged. Please reload the page and try again.';
  }

  window.__awCartNet = {
    read: read,
    readSoft: readSoft,
    post: post,
    addItems: addItems,
    announce: announce,
    invalidate: invalidate,
    qtyOf: qtyOf,
    message: message,
  };
  return window.__awCartNet;
})();

const dataEl = document.getElementById('aw-gf-data');
const rootEl = document.getElementById('AwGuidedFlow');
if (dataEl && rootEl) init(JSON.parse(dataEl.textContent));

/* One money format ("$104" / "$23.50"). Mirrors aw-money.liquid and usd() in
 * aw-cart-sync.js; keep in sync. */
function usd(cents) {
  const n = cents / 100;
  const digits = n % 1 === 0 ? 0 : 2;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: 2 });
}

/* Composition -> first-order code. IDENTICAL to aw-order-widget.js and
 * aw-cart-sync.js; keep the three in sync. */
/* The only discount codes this theme owns. Anything else attached to the cart
 * belongs to the customer (a typed promo) or a referral/ambassador link
 * (LeadDyno / UpPromote); the cart API's discount field REPLACES the whole code
 * set, so a blind write strips it and kills that order's attribution and the
 * partner's commission. Fix 2026-07-20. */
const THEME_CODES = ['starter22', 'home77', 'family33'];

function offerCode(waterQty, hasDispenser) {
  const eligible = waterQty + (hasDispenser ? 1 : 0);
  if (waterQty < 1) return null;
  if (waterQty <= 3) return eligible >= 3 ? 'starter22' : null;
  /* 2026-08-18 (Brian): home77 extended from <=5 to <=6 so the chart-matched
   * 6-jug default keeps the FREE DISPENSER as the go-to offer. Verified in
   * admin: home77 is a Buy-X-Get-Y (buy 4+ water jugs -> dispenser 100% off,
   * no upper limit) so a 6-jug cart validates; at exactly 6+dispenser it also
   * pays the customer more than family33 ($77.00 vs $76.89). family33 (33%,
   * admin minimum 7 items) now starts at 8 jugs, or 7+ eligible items.
   * starter22 stays the sub-4-jug offer, unchanged. */
  if (waterQty <= 6) return hasDispenser && eligible >= 5 ? 'home77' : null;
  return eligible >= 7 ? 'family33' : null;
}

/* 2026-08-08, promoted to a shared helper 2026-08-09. The published jug size
 * plus the per-delivery total, recomputed live from the quantity. ONE expression,
 * read by BOTH the plan card and the adjust panel, so the two lines cannot drift
 * (2 -> 5, 4 -> 10, 6 -> 15, 8 -> 20 gallons). House rule: "glass jugs", never
 * "bottles". */
function volumeText(qty) {
  const gallons = qty * 2.5;
  const g = gallons % 1 === 0 ? String(gallons) : gallons.toFixed(1);
  return '2.5 gallons per glass jug, ' + g + ' gallons per delivery';
}

function clearDiscountCookie() {
  const expire = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = expire;
  document.cookie = expire + '; domain=.' + location.hostname.replace(/^www\./, '');
}

/* ZIP prefix -> region variant key: 005 + 070-089 + 100-149 -> NY/NJ,
 * 970-979 + 986 (Vancouver WA rides Portland routes) -> OR, else CA/FL/TX
 * (B's select default). String input keeps leading-zero ZIPs safe.
 * SURCHARGE RE-ENABLE requires auditing this map first - see theme-c/README.md. */
function zipRegionKey(zip) {
  const p = Number(String(zip).slice(0, 3));
  if (p === 5 || (p >= 70 && p <= 89) || (p >= 100 && p <= 149)) return 'NY/NJ';
  if ((p >= 970 && p <= 979) || p === 986) return 'OR';
  return 'CA/FL/TX';
}

/* Task 10 (2026-08-05): zone naming for the rebuilt flow.
 *
 * The wizard's steps were replaced (plan card, adjust panel, extras), so the
 * events need names that describe what a customer now actually does. This is
 * ADDITIVE ONLY: gf_step_view keeps posting its original `step` and `sub`
 * fields byte-identically, so every saved query and dashboard keyed on them
 * keeps working and the pre-rebuild baseline stays comparable. `zone` is a
 * stable, human-readable label layered on top - nothing was renamed or removed,
 * and no new event or analytics destination was introduced.
 *
 *   step 1            -> 'area'      (ZIP / delivery-area panel)
 *   step 3 sub plan   -> 'plan'      (the plan card, the served-ZIP landing state)
 *   step 3 sub adjust -> 'adjust'    (the adjust panel)
 *   step 4            -> 'extras'    (one-time accessories)
 *   step 5            -> 'checkout'  (reserved, see below)
 *
 * goTo(3) with no sub resolves to 'plan' because a non-boot entry to step 3
 * always lands on the plan card (goTo calls awGfShowSub('plan') below).
 *
 * 'checkout' is in the vocabulary but is NOT emitted today: toCheckout() marks
 * dot 5 and announces it, yet has never fired a gf_step_view. Adding one would
 * invent gf_step_view volume that did not exist before the rebuild and would
 * break exactly the pre/post comparison this change is protecting. Left as a
 * deliberate one-line follow-up rather than a silent baseline shift.
 *
 * Shared by both gf_step_view call sites (init()'s goTo and the awGfShowSub
 * IIFE at the bottom of this file), so the two can never drift apart. */
function gfZone(step, sub) {
  if (Number(step) === 3) return sub === 'adjust' ? 'adjust' : 'plan';
  return { 1: 'area', 4: 'extras', 5: 'checkout' }[Number(step)] || null;
}

function init(D) {
  // Full B-parity ladder (2026-07-20): trial sizes 1-3 (first delivery only)
  // + core 4-12 + self-serve bulk 16-32, matching B's aw-order-widget.liquid
  // quantity groups exactly. Pricing needs NO change: perJugCents already
  // bills the published curve, caps >12 at the 12-jug price, and bills
  // trials at the base jug price; offerCode and the 1:1 deposit match are
  // quantity-driven. Renewals for bulk bill qty x the 12-jug price, same as
  // B's aw-cart-sync curvePer().
  const QSTEPS = [1, 2, 3, 4, 6, 8, 10, 12, 16, 20, 24, 28, 32];
  const jugsWord = (n) => n + ' glass jug' + (n === 1 ? '' : 's');

  /* Trial-size renewal disclosure (2026-08-07). Brian: "check our prior variants,
   * the trial plan had a notice that the subscription renews at our 4 jug min".
   *
   * He is right, and the notice is real. E7 and E10 (LIVE today) still ship it;
   * Task 9 deleted it from E8 along with the review panel, so an E8 trial shopper
   * currently gets NO renewal disclosure anywhere in the flow. E10's wording:
   *   "Trial size: your first delivery is 2 glass jugs. After your trial,
   *    deliveries continue at our 4 jug minimum, $104 per delivery. A one time
   *    $66 refundable deposit top up applies with your first full delivery."
   *
   * THE CLAIM WAS CHECKED AGAINST REAL ORDERS BEFORE BEING REPRINTED, because a
   * 2 jug trial that silently renews larger is a genuine transparency problem.
   * Full census of 13,195 orders, 2026-06-07 to 2026-08-06, classified by the
   * Appstle tags appstle_subscription_recurring_order / _first_order:
   *   - 9,912 renewals. 9,861 of them (99.49%) bill 4 or more jugs. Only 51
   *     (0.51%) bill 1 to 3, and those are grandfathered contracts, not trial
   *     conversions (median $21.00 per jug, only 4 of the 51 at the $26 curve).
   *   - So the "4 jug minimum" IS what happens. Restored, not softened.
   *   - "$104 per delivery" is right going FORWARD: of qty-4 renewals placed on
   *     or after the Jul 22 increase, 93.1% billed $104. Before Jul 22 almost
   *     none did (93.9% billed the legacy $96), which is why the figure below is
   *     COMPUTED from perJugCents() rather than hardcoded - it follows the
   *     published curve and cannot go stale the way a literal would.
   *   - 127 of 565 first orders (22.5%) start below 4 jugs, so this notice is on
   *     the path of roughly one new customer in five. It is not an edge case.
   *
   * NOT independently verified, inherited from the live E10 copy: that the
   * deposit top up is actually collected on the first full delivery. Flagged in
   * the report rather than dropped, since it is already the live claim.
   *
   * RESOLVED 2026-08-07, pre-launch audit CRITICAL-2 - see headlineCents(). */
  /* The renewal figure, single-sourced (2026-08-07).
   *
   * Two surfaces now state what a customer pays on every delivery AFTER the
   * first: the trial notice and the "Future deliveries" block restored to the
   * plan card. They must never disagree, so both read this one expression.
   *
   * At qty >= 4 the renewal IS the plan's own price - q.recurringCents, which
   * awzQuote() keeps CURVE-based even when an offer code suppresses the curve
   * on the first order (renewals bill the curve; renewal-price-sentinel checks
   * that daily). Offers are first-order-only and never touch this figure.
   *
   * At qty < 4 a naive qty x unit price would be a lie. The 13,195-order census
   * above is unambiguous: 99.49% of renewals bill 4 or more jugs, and 93.1% of
   * post-Jul-22 qty-4 renewals billed $104. A 2 jug trial renews at the 4 jug
   * minimum, so the block shows the 4 jug cell - the same number the trial
   * notice inches below it prints and the same one aw-cart-sync.js
   * updateRenewal() puts in the drawer via Math.max(qty, 4).
   *
   * The surcharge fee rides on both arms because the gate's own copy says it
   * applies to EACH delivery, not just the first. */
  function renew4Cents(reg, fee) { const m = contMinQty(); return perJugCents(reg, m) * m + fee; }
  function renewalCents(q, reg, fee, trial) {
    return trial ? renew4Cents(reg, fee) : q.recurringCents + fee;
  }

  function trialNoteText(reg, fee) {
    const m = contMinQty();                            // 8 on San Diego, else 4
    const renewM = renew4Cents(reg, fee);              // the published minimum cell
    const topup = D.depositCents * (m - state.qty);    // deposits are 1 per jug
    return 'Trial size: your first delivery is ' + jugsWord(state.qty)
      + '. After your trial, deliveries continue at our ' + m + ' jug minimum, ' + usd(renewM)
      + ' per delivery. A one time ' + usd(topup)
      + ' refundable deposit top up applies with your first full delivery.';
  }

  function paintTrialNote(el, trial, reg, fee) {
    if (!el) return;
    el.hidden = !trial;
    if (trial) el.textContent = trialNoteText(reg, fee);
  }

  /* The card's headline figure (2026-08-07, pre-launch audit CRITICAL-2).
   *
   * THE DEFECT. At qty 2 the biggest number on the card read "$52 first
   * delivery". $52 is recurringCents - the WATER SUBTOTAL - so it was below the
   * $166.62 the cart actually charges that day, and below the $104 the trial
   * notice and the drawer both say the plan renews at. Read either way, the
   * card's most prominent number sat under the charge. That is the exact defect
   * class this rebuild exists to eliminate. Two labels also both read "first
   * delivery" while showing different numbers ($52 headline vs $166.62 total
   * row), which is its own defect. 22.5% of first orders start below 4 jugs.
   *
   * THE RULE. At trial sizes the headline is what the customer pays TODAY -
   * q.todayCents + fee, the identical expression the "First delivery" total row
   * uses and the figure verified equal to /cart.js total_price. Same label, same
   * value, in both places, so no label can be duplicated with two values.
   *
   * WHY today rather than the renewal. The recurring price is not a property of
   * the plan the customer is choosing at qty < 4 - it jumps to the 4 jug minimum
   * on the very next delivery. The renewal is therefore stated where it belongs
   * and only once: trialNoteText() directly beneath the price ("deliveries
   * continue at our 4 jug minimum, $104 per delivery"), which is computed from
   * perJugCents() and agrees with aw-cart-sync.js updateRenewal()'s
   * Math.max(qty, 4) drawer figure. The card, the notice and the drawer now all
   * say the same two numbers.
   *
   * At qty >= 4 nothing changes: the headline stays recurringCents + fee with the
   * cadence label, which is correct there (recurring == the plan's own price) and
   * is what shipped and measured clean. */
  function headlineCents(q, fee, trial) {
    return trial ? q.todayCents + fee : q.recurringCents + fee;
  }



  const state = {
    // 2026-08-08: step 1 is deleted; the plan card is the flow's first step.
    step: 3,
    household: null,
    picked: false, // a plan was chosen (step 2 answer or tier table)
    /* 2026-08-18 (Brian, second ruling same day): the card presents the 4-jug
     * MINIMUM as the starting point - the step heading says so - and the usage
     * chart + adjust hint do the up-sizing. The free-dispenser offer still
     * covers a customer who sizes up to 6 (see offerCode). Cadence defaults to
     * every 4 weeks. */
    qty: 4,
    weeks: 4,
    planId: null,
    dispenser: true,
    shelf: false,
    stand: false,
    spigot: false,
    shelfWhite: false,
    eligibility: readEligibility(),
  };

  const $ = (sel) => rootEl.querySelector(sel);
  const $$ = (sel) => Array.from(rootEl.querySelectorAll(sel));
  const out = (name) => $('[data-out="' + name + '"]');

  const panels = $$('.aw-gf__step');
  const dots = $$('[data-gf-dot]');
  // F2 fix (2026-08-05), re-cut 2026-08-08 when step 1 was deleted and the rail
  // became Plan / Extras / Checkout. The aria-live announcer's "Step X of N"
  // has to match the 3-dot rail the customer can actually see.
  // This maps the LOAD-BEARING internal step id (3/4/5-checkout, read by
  // sessionStorage resume and by gf_step_view's { step } payload - never
  // renumbered, so the funnel stays comparable across builds) to its 1-based
  // position on the rail, for this announcement string only. gf_step_view below
  // keeps posting the raw internal step id.
  const DISPLAY_STEP = { 3: 1, 4: 2, 5: 3 };
  const DISPLAY_TOTAL = 3;
  const announceEl = $('#aw-gf-announce');
  // 2026-08-08: the delivery-ZIP context bar, the only remaining route back to
  // the gate now that the rail's Area step is gone.
  const zipBar = $('[data-awz-zipbar]');
  // Task 9 (2026-08-05): the legacy step-2 household panel and the old
  // water/setup/review sub-panels are deleted, along with every element handle
  // that only served them (freqMore, the setup checkboxes, the dispenser
  // toggle, the review panel's #aw-gf-add). The plan card's CTA is the one add
  // path now; render() mirrors state onto it.
  // Task 6: the plan card (the served-ZIP landing state). Card writes are
  // scoped to planEl.
  const planEl = $('[data-awz-plan]');
  const planCta = planEl ? planEl.querySelector('[data-gf-submit]') : null;
  // In-flight guard, mirroring the control arm. render() runs on every control
  // change (stepper, frequency chip, dispenser toggle, shelf, tier table, back)
  // and used to unconditionally re-enable the CTA and reset its label, so a
  // customer who adjusted anything mid-add got a live button back and could fire
  // a second add that merged with the first - doubled jugs, doubled deposits and
  // a doubled RECURRING subscription.
  let adding = false;
  const allEl = $('#aw-gf-all');
  const gateEl = document.getElementById('aw-zip-gate');
  // Persistent route-note banner, rendered by the section OUTSIDE the step
  // panels. The gate paints the Oregon "delivery starts Q4 2026" note into its
  // own result node inside step 1, which is hidden from step 2 on and never
  // seen by a visitor whose stored ZIP boots them past it. Brian, 2026-07-21.
  const noteEl = document.getElementById('aw-gf-eligibility-note');
  // Offer stand-down state (Brian, 2026-07-21). applyOffer refuses to write our
  // code when a referral/ambassador/typed code already owns the cart, but the
  // summary kept promising the discount anyway, so a referral customer was shown
  // a total up to $77 below what checkout charges. null = our offer is live, a
  // string = the foreign code that wins ('' when the cart does not name it).
  // Sticky for the session because the stand-down also removes aw_offer_code;
  // applyOffer clears it again if the cart's codes change.
  let foreignCode = null;

  function readEligibility() {
    try { return JSON.parse(localStorage.getItem('aw_delivery')) || null; } catch (_) { return null; }
  }

  const KEY = 'aw_gf_v1';
  function save() {
    const { step, household, picked, qty, weeks, planId, planOv, dispenser, shelf, stand, spigot, shelfWhite } = state;
    try { sessionStorage.setItem(KEY, JSON.stringify({ step, household, picked, qty, weeks, planId, planOv, dispenser, shelf, stand, spigot, shelfWhite })); } catch (_) {}
  }
  function restore() {
    let had = false;
    try {
      const s = JSON.parse(sessionStorage.getItem(KEY));
      if (s) { Object.assign(state, s); had = true; }
    } catch (_) {}
    if (QSTEPS.indexOf(state.qty) === -1) state.qty = 4; // 4-jug minimum is the starting point (2026-08-18)
    return had;
  }

  const canOrder = () => !!(state.eligibility && state.eligibility.state !== 'waitlist');

  function region() {
    const key = state.eligibility && state.eligibility.zip ? zipRegionKey(state.eligibility.zip) : 'CA/FL/TX';
    // Fall back to any region rather than crash if a variant title is renamed.
    return D.regions[key] || D.regions['CA/FL/TX'] || D.regions[Object.keys(D.regions)[0]];
  }

  function planForWeeks(weeks) {
    const re = new RegExp('every\\s+' + weeks + '\\s+week', 'i');
    const one = weeks === 1 ? D.plans.find((p) => /every\s+week/i.test(p.name)) : null;
    return D.plans.find((p) => re.test(p.name)) || one || null;
  }

  function routeWeeks() {
    const el = state.eligibility || readEligibility();
    const w = el && el.state === 'served' && el.cadence && Number(el.cadence.weeks);
    return w > 0 ? w : null;
  }

  /* Route-level quantity floor (Brian, 2026-08-18): the San Diego run (3-week
   * cadence, 107 ZIPs in aw-zip-regions.json) requires at least 8 jugs per
   * RECURRING delivery. Trial sizes (1-3 jugs, first delivery only) stay
   * available - that is the "except on their first delivery" carve-out; there
   * is no per-cycle quantity mechanism in a subscription, so a sub-minimum
   * FIRST recurring order cannot exist either. Keyed by the route label the
   * gate stores on the eligibility verdict (aw-zip-gate.js resolve());
   * rename the region there and this key must follow. */
  const ROUTE_MIN_QTY = { 'San Diego': 8 };
  function routeMinQty() {
    const E = state.eligibility;
    const label = canOrder() && E && E.cadence && E.cadence.label;
    return (label && ROUTE_MIN_QTY[label]) || 0;
  }
  /* The quantity deliveries CONTINUE at after a trial: the route minimum
   * where one exists (San Diego: 8), the standard 4 jug minimum elsewhere. */
  function contMinQty() { return routeMinQty() || 4; }
  /* Trial = any first-delivery size below the route's continuing minimum.
   * Brian, 2026-08-18: San Diego may START at 6, 4 or 2 jugs for one
   * delivery, then continues at the 8 jug route minimum - the same contract
   * the 1-3 jug trial always made, with SD's larger minimum. */
  function isTrialQty(q) { return q < contMinQty(); }

  // Region-cadence enforcement, ported from B2's aw-order-widget.js
  // (2026-07-19 parity fix): in cadence regions the frequency choices are
  // limited to multiples of the route cadence, so a subscription can never
  // bill faster than its route runs. Owns ALL chip visibility - a chip also
  // hides when no Appstle plan backs its cadence. Task 7: the adjust panel's
  // cadence chips (.awz-cch) are governed HERE too, by the same expressions -
  // a chip for a nonexistent plan breaks add-to-cart, so nothing else may
  // decide which cadence chips render.
  function applyCadenceUI() {
    const rw = routeWeeks();
    $$('.awz-cch').forEach((b) => {
      const noPlan = !planForWeeks(Number(b.dataset.weeks));
      b.hidden = noPlan || (!!rw && Number(b.dataset.weeks) % rw !== 0);
    });
    if (rw && state.weeks % rw !== 0) setWeeks(rw);
  }

  const onCurve = (reg) => String(reg.id) === String(D.curveVariantId);

  function perJugCents(reg, qty) {
    if (!onCurve(reg)) return reg.cents;
    if (D.curve[String(qty)]) return D.curve[String(qty)];
    if (qty > 12) return D.curve['12'];
    return reg.cents; // trial sizes bill the base jug price
  }

  /* Single money-truth for the first order (Task 5; verified on a real cart
   * 2026-08-05). With one of our offer codes active the Appstle volume curve
   * is SUPPRESSED: water bills at the full catalogue price (reg.cents) and the
   * offer applies to water + dispenser, allocated PER LINE (Math.round each,
   * which is exactly how Shopify's own allocation lands - 6 jugs + dispenser
   * on family33: water $156.00 -$51.48, dispenser $77.00 -$25.41, deposits
   * untouched, total $354.11). With no offer the curve applies. Deposits never
   * receive any allocation. recurringCents stays CURVE-based regardless:
   * renewals bill the volume curve as a discount (renewal-price-sentinel
   * verifies that daily). Everything that renders a first-order figure reads
   * from here and nothing else. */
  function awzQuote(st) {
    const reg = region();
    const rawCode = D.offersLive && D.customerEligible ? offerCode(st.qty, st.dispenser) : null;
    // applyOffer stood down for a code the customer brought with them, so ours
    // is never written: quote as if no offer, or we under-quote the real total.
    const code = foreignCode === null ? rawCode : null;
    const recurringCents = perJugCents(reg, st.qty) * st.qty;
    const waterCents = code ? reg.cents * st.qty : recurringCents;
    const depositCents = D.depositCents * st.qty;
    const dispenserCents = st.dispenser ? D.dispenserCents : 0;
    let discountCents = 0;
    if (code === 'starter22') discountCents = Math.round(0.22 * waterCents) + Math.round(0.22 * dispenserCents);
    else if (code === 'home77') discountCents = D.dispenserCents;
    else if (code === 'family33') discountCents = Math.round(0.33 * waterCents) + Math.round(0.33 * dispenserCents);
    return {
      code, rawCode, waterCents, depositCents, dispenserCents, discountCents,
      todayCents: waterCents + depositCents + dispenserCents - discountCents,
      recurringCents,
    };
  }

  /* 2026-08-18 (Brian): the "See all plans + pricing" tier table was rendered
   * once server-side from the CA/FL/TX curve, so a customer whose ZIP resolves
   * to New York/New Jersey (flat $29) or Oregon (flat $22) read California
   * prices there and the panel never said otherwise. Repaint every row from the
   * same perJugCents()/region() the plan card uses, so the two cannot disagree.
   * No ZIP yet -> region() falls back to CA/FL/TX and this repaints the same
   * curve values the server rendered. */
  const REGION_LABELS = { 'CA/FL/TX': 'California, Florida and Texas', 'OR': 'Oregon', 'NY/NJ': 'New York and New Jersey' };
  function paintTierTable() {
    const reg = region();
    const tierMin = routeMinQty();
    document.querySelectorAll('#aw-gf-tier-table tr[data-gf-tier]').forEach((tr) => {
      const q = Number(tr.dataset.gfTier);
      tr.hidden = !!tierMin && q < tierMin;
      const per = perJugCents(reg, q);
      const perEl = tr.querySelector('[data-gf-per]');
      const delEl = tr.querySelector('[data-gf-del]');
      if (perEl) perEl.textContent = usd(per);
      if (delEl) delEl.textContent = usd(per * q);
    });
    const note = document.querySelector('[data-gf-region-note]');
    if (note) {
      const key = Object.keys(D.regions).find((k) => D.regions[k] === reg);
      const txt = onCurve(reg)
        ? 'Prices shown are for California, Florida and Texas delivery. Oregon, New York and New Jersey prices appear here and on your plan card once your ZIP is checked.'
        : 'Prices shown are for ' + (REGION_LABELS[key] || key) + ' delivery, based on your ZIP.';
      if (note.textContent !== txt) note.textContent = txt;
    }
    // Route-minimum routes offer trial sizes 2 / 4 / 6 instead of the
    // standard 1 / 2 / 3 (San Diego, 2026-08-18). Rewrite the picker's
    // three buttons and caption; restore the standard set elsewhere.
    const contMin = contMinQty();
    const sizes = contMin > 4 ? [2, 4, 6] : [1, 2, 3];
    document.querySelectorAll('.aw-gf__qty-picker [data-gf-qty-only]').forEach((b, i) => {
      const s = sizes[i];
      if (!s) return;
      if (Number(b.dataset.gfChoose) !== s) {
        b.dataset.gfChoose = String(s);
        b.textContent = s;
        b.setAttribute('aria-label', 'Choose ' + s + (s === 1 ? ' jug' : ' jugs') + ' for your first delivery');
      }
    });
    const tnote = document.querySelector('[data-gf-trial-note]');
    if (tnote) {
      const t = contMin > 4
        ? 'Trial size (2, 4 or 6 jugs, first delivery only)'
        : 'Trial size (1 to 3 jugs, first delivery only)';
      if (tnote.textContent !== t) tnote.textContent = t;
    }
    const mn = document.querySelector('.aw-gf__minnote');
    if (mn) {
      const t = 'Plans start at our ' + contMin + ' jug minimum per delivery. The usage chart below shows what most households drink.';
      if (mn.textContent !== t) mn.textContent = t;
    }
    const qh = document.querySelector('[data-gf-qty-hint]');
    if (qh) {
      const wk = (state.eligibility && state.eligibility.cadence && state.eligibility.cadence.weeks) || 0;
      const t = contMin > 4
        ? 'This route delivers every ' + wk + ' weeks with an ' + contMin + ' jug minimum. 2, 4 or 6 jugs are available for your first delivery only.'
        : '6 glass jugs every 4 weeks is the average for one adult (two adults: 12). 2 is a trial.';
      if (qh.textContent !== t) qh.textContent = t;
    }
  }

  const announce = (text) => { if (announceEl) announceEl.textContent = text; };

  function markDot(n) {
    dots.forEach((d) => {
      const i = Number(d.dataset.gfDot);
      const done = i < n;
      d.classList.toggle('is-current', i === n);
      d.classList.toggle('is-done', done);
      if (i === n) d.setAttribute('aria-current', 'step');
      else d.removeAttribute('aria-current');
      /* 2026-08-08: a COMPLETED step is real backward navigation; a step ahead
       * of the customer is not (you cannot jump to Checkout before choosing a
       * plan). The distinction is made three ways, on purpose: aria-disabled so
       * assistive tech is told the truth, tabindex so the keyboard does not stop
       * on dead controls, and a visible underline + pointer on .is-done in
       * aw-wizard.css. This store already carries a rage-click defect where a
       * non-interactive element looks tappable, so "looks the same, does
       * nothing" is exactly the outcome being avoided. */
      const navBtn = d.querySelector('[data-gf-nav]');
      if (navBtn) {
        navBtn.setAttribute('aria-disabled', done ? 'false' : 'true');
        navBtn.tabIndex = done ? 0 : -1;
      }
    });
  }

  function goTo(n, opts) {
    const same = state.step === n;
    state.step = n;
    save();
    // 2026-08-04: per-step funnel event via the gate's tracker (guarded - measurement
    // must never break the flow). First per-step instrumentation on this store.
    // Task 10 (2026-08-05): `zone` added; `step` unchanged. The !same guard is
    // what keeps this to one event per real transition - a re-render never
    // reaches here, and a boot resume still stays silent.
    if (!(opts && opts.boot) && !same) {
      try { if (typeof window.awTrackGate === 'function') window.awTrackGate('gf_step_view', { gate: 'guided_flow', step: n, zone: gfZone(n) }); } catch (_) {}
    }
    // 2026-08-05 journey pass (trimmed by Task 9): entering step 3 fresh always
    // lands on the plan card; a boot resume restores the visitor's stored
    // sub-panel (awGfShowSub guards stale keys back to the plan card).
    if (n === 3) {
      if (window.awGfShowSub) window.awGfShowSub((opts && opts.boot) ? null : 'plan', false);
    }
    panels.forEach((p) => { p.hidden = Number(p.dataset.gfStep) !== n; });
    markDot(n);
    render();
    if (!(opts && opts.boot) && !same) {
      const h = document.querySelector('#aw-gf-step' + n + ' .aw-gf__h');
      announce('Step ' + (DISPLAY_STEP[n] || n) + ' of ' + DISPLAY_TOTAL + ': ' + (h ? h.textContent : ''));
      if (h) h.focus();
    }
  }

  function render() {
    document.body.classList.toggle('aw-flow-active', canOrder() && state.step >= 2);
    // Task 8: extras-step confirmation line mirrors the plan that was just added.
    const xconf = document.querySelector('[data-awz-confirm-text]');
    // 2026-08-09: weekly is selectable now, so this can no longer say "every 1
    // weeks". Mirrors the same singular rule render() already applies below.
    if (xconf) xconf.textContent = state.qty + (state.qty === 1 ? ' jug' : ' jugs')
      + ', ' + (state.weeks === 1 ? 'every week' : 'every ' + state.weeks + ' weeks') + ' - added';
    const E = state.eligibility;
    const reg = region();
    /* San Diego floor, revised same day (Brian, 2026-08-18): a RECURRING
     * quantity below the route minimum bumps up to it (the default lands a
     * fresh SD visitor on the 8 jug plan), but an explicitly chosen 6, 4 or
     * 2 stays as a one-delivery trial that then continues at the route
     * minimum - the same contract the 1-3 jug trial always made. */
    const routeMin = routeMinQty();
    if (routeMin && state.qty < routeMin && state.household !== 'trial') { state.qty = routeMin; save(); }
    else if (isTrialQty(state.qty)) { if (state.household !== 'trial') { state.household = 'trial'; save(); } }
    else if (state.household === 'trial') { state.household = null; save(); }
    // Sub-minimum chips stay VISIBLE on route-minimum routes: tapping 2, 4
    // or 6 is a trial pick there (the card relabels instantly). Explicit
    // unhide self-heals hiding applied by the earlier bump-only rule
    // within the same page view.
    $$('.awz-qch').forEach((b) => { b.hidden = false; });
    // First-order money comes from awzQuote and NOTHING else (Task 5): it owns
    // the curve-vs-catalogue rule, the per-line offer math and the stand-down
    // for a foreign code. One-time extras and the surcharge fee ride on top;
    // neither is ever discounted, and deposits never discount.
    // All three codes are new-customers-only and one use per customer, so a
    // logged-in customer who has already ordered can never redeem one. Quoting
    // it anyway loses them up to $77 between the card and checkout. Guests stay
    // eligible here because Shopify only resolves identity at email entry, so
    // their wording states the condition instead of promising. Brian, 2026-07-21.
    const q = awzQuote(state);
    const water = q.waterCents;
    const deposits = q.depositCents;
    const fee = E && E.state === 'surcharge' ? E.fee * 100 : 0;
    const code = q.code;
    const discount = q.discountCents;

    const cadence = state.weeks === 1 ? 'every week' : 'every ' + state.weeks + ' weeks';
    const trial = isTrialQty(state.qty);

    /* 2026-08-09 (Brian): the route cadence, stated prominently on the plan card.
     * Read off the SAME stored verdict the ZIP gate wrote (E.cadence), never
     * recomputed here, so the gate and the card cannot disagree. Names the area
     * as well as the interval: the plan title says "every 3 weeks" but cannot
     * convey that the 3 weeks is a property of the ROUTE. Hidden outright for a
     * ZIP with no route zone rather than inventing an interval. */
    const routeOut = document.querySelector('[data-out="route-cadence"]');
    if (routeOut) {
      const cad = canOrder() && E && E.cadence && Number(E.cadence.weeks) > 0 ? E.cadence : null;
      const every = cad ? (Number(cad.weeks) === 1 ? 'every week' : 'every ' + cad.weeks + ' weeks') : '';
      const mq = routeMinQty();
      const routeTxt = cad
        ? 'We deliver to ' + cad.label + ' ' + every + '. Your deliveries follow that schedule.'
          + (mq ? ' This route has an ' + mq + ' jug minimum per delivery; your first delivery can be 2, 4 or 6 jugs.' : '')
        : '';
      if (routeOut.textContent !== routeTxt) routeOut.textContent = routeTxt;
      routeOut.hidden = !routeTxt;
    }

    // Task 9 (2026-08-05): every write that only served the deleted
    // water/setup/review panels is gone (stepper, qty notes, review money rows,
    // trial note, dispenser toggle chrome, setup headline). The plan card and
    // adjust panel blocks below are the whole rendered surface of step 3 now.
    if (noteEl) {
      const routeNote = E && E.note && E.note.note ? E.note.note + (E.note.cta ? ' ' + E.note.cta : '') : '';
      // The gate only paints the note in its SERVED branch: a surcharge result
      // prints the fee sentence and drops the note entirely, so suppressing the
      // banner on step 1 for every state left a surcharge ZIP with a note
      // showing nowhere at all. Step 1 now hides this banner only when the gate
      // is the one showing the note. Unhide BEFORE writing: text written while
      // the element is hidden is not in the a11y tree, so it is never announced.
      noteEl.hidden = !routeNote || (state.step < 2 && E.state === 'served');
      // render() runs on every control change and this is an aria-live region,
      // so an unconditional write re-announced the note on every keystroke-level
      // change. Write only when the note actually changes.
      if (noteEl.textContent !== routeNote) noteEl.textContent = routeNote;
    }

    /* 2026-08-08: the delivery-ZIP context bar. Shown only once there is an
     * ORDERABLE verdict - a waitlist result clears the stored verdict outright,
     * and a visitor with no ZIP has the hero gate expanded in front of them
     * already (render() drops .aw-flow-active below), so a bar reading
     * "Delivering to" would be a lie in both states. */
    if (zipBar) {
      const zipNow = canOrder() && E && E.zip ? String(E.zip) : '';
      zipBar.hidden = !zipNow;
      const zipOut = zipBar.querySelector('[data-out="zip-current"]');
      if (zipOut && zipOut.textContent !== zipNow) zipOut.textContent = zipNow;

    }
    const waitlisted = E && E.state === 'waitlist';
    const needsZip = !E && !!gateEl;
    const regionOut = canOrder() && reg.available === false; // B disables the option; C disables the CTA

    // Task 6: the plan card. Every figure comes from awzQuote (q) and nothing
    // else, rewritten on every render. pout() never leaves [data-awz-plan]; the
    // card sits LAST inside .aw-gf-card so the first-match out() helper above
    // still resolves the water/review panels' duplicate names.
    if (planEl) {
      const pout = (n) => planEl.querySelector('[data-out="' + n + '"]');
      pout('plan-title').textContent = trial
        ? jugsWord(state.qty) + ', one trial delivery'
        : jugsWord(state.qty) + ', ' + cadence;
      // 2026-08-09: the jug size + per-delivery volume, moved onto the card so a
      // customer who never opens Adjust still learns how much water this is. Same
      // volumeText() the adjust panel's copy uses - one expression, two panels,
      // which are never on screen together (awGfShowSub shows one .aw-gf-sub).
      const planVol = pout('volume');
      if (planVol) {
        const pvTxt = volumeText(state.qty);
        if (planVol.textContent !== pvTxt) planVol.textContent = pvTxt;
      }
      // Fix (2026-08-05): a surcharge-ZIP fee applies to EACH delivery per the
      // gate copy ("...a $X delivery fee applies to each delivery"), so it
      // belongs in the recurring figure too, not just today's total. `fee` is
      // 0 for every non-surcharge state, so this is a no-op everywhere else.
      pout('recurring').textContent = usd(headlineCents(q, fee, trial));
      pout('cadence').textContent = trial ? 'first delivery' : cadence;
      paintTrialNote(pout('trial-note'), trial, reg, fee);
      pout('deposit').textContent = usd(q.depositCents);
      pout('water-label').textContent = 'Water, ' + state.qty + (state.qty === 1 ? ' jug' : ' jugs');
      pout('water').textContent = usd(water);
      pout('deposit-line').textContent = usd(deposits);
      // FREE is only ever claimed when the live quote's code makes the
      // dispenser free (home77) - same rule as the setup panel (2026-08-05).
      const planDispFree = state.dispenser && foreignCode === null && code === 'home77';
      const planDispRow = planEl.querySelector('[data-awz-row="dispenser"]');
      if (planDispRow) {
        planDispRow.hidden = !state.dispenser;
        const wasEl = planDispRow.querySelector('[data-out="disp-was"]');
        const freeEl = planDispRow.querySelector('.awz-free');
        if (wasEl) { wasEl.textContent = usd(D.dispenserCents); wasEl.classList.toggle('awz-nostrike', !planDispFree); }
        if (freeEl) freeEl.hidden = !planDispFree;
      }
      const planSub = planEl.querySelector('.awz-plan__sub');
      if (planSub) {
        planSub.hidden = !state.dispenser;
        const inc = planSub.querySelector('.awz-free');
        if (inc) {
          inc.textContent = planDispFree ? 'included free' : 'included, ' + usd(D.dispenserCents);
          inc.classList.toggle('awz-free--muted', !planDispFree);
        }
      }
      // starter22/family33 discount water + dispenser without making the
      // dispenser free: named on its own line so the lines always sum to the
      // First delivery total.
      // Fix (2026-08-05, reviewed finding): the card's own total must be
      // honest for a surcharge ZIP even when a visitor uses the card's CTA
      // without ever seeing the review panel, which already includes the fee
      // (q.todayCents + extras + fee) above. Mirrors the [data-row="fee"]
      // pattern already used there - hidden by default, unhidden only when
      // fee > 0 (i.e. E.state === 'surcharge').
      const planFeeRow = planEl.querySelector('[data-awz-row="fee"]');
      if (planFeeRow) {
        planFeeRow.hidden = fee === 0;
        if (fee > 0) planFeeRow.querySelector('[data-out="fee-line"]').textContent = usd(fee);
      }
      const planOfferRow = planEl.querySelector('[data-awz-row="offer"]');
      if (planOfferRow) {
        const showOffer = discount > 0 && code !== 'home77';
        planOfferRow.hidden = !showOffer;
        if (showOffer) {
          planOfferRow.querySelector('[data-out="offer-name"]').textContent =
            code === 'starter22' ? 'Starter offer, 22% off' : 'Family offer, 33% off';
          planOfferRow.querySelector('[data-out="offer-off"]').textContent = '-' + usd(discount);
        }
      }
      // FIX 1 (2026-08-07): the offer gate in the section liquid resolves only for a
      // SIGNED-IN customer, so a logged-out returning subscriber is quoted an offer the
      // checkout will refuse. The shipped mitigation is a qualifier, not a sign-in gate
      // (a sign-in gate would hide the offer from new customers, who have no account).
      //
      // 2026-08-09 (operator): the qualifier moved off the top of the card and onto the
      // LINE IT QUALIFIES. The card claims an offer in exactly two mutually exclusive
      // ways, so there are two anchors and the live one owns the copy:
      //   planDispFree  -> home77 makes the dispenser FREE; the named offer row is hidden.
      //   showOffer     -> starter22/family33 name a percentage row; the dispenser reverts
      //                    to an unstruck $77, so the qualifier must NOT sit on it there.
      // The union of the two conditions is exactly the single condition this block used
      // before, so a card quoting plain published pricing still shows nothing anywhere,
      // and the two can never both be visible. No new state, same two flags as above.
      const dispOfferNote = planEl.querySelector('[data-awz-offer-note="dispenser"]');
      if (dispOfferNote) dispOfferNote.hidden = !planDispFree;
      const rowOfferNote = planEl.querySelector('[data-awz-offer-note="offer"]');
      if (rowOfferNote) rowOfferNote.hidden = !(discount > 0 && code !== 'home77');
      pout('today').textContent = usd(q.todayCents + fee);
      // 2026-08-06 (Brian: "remove dispense option Should be there"). One quiet
      // control, one state field. awzQuote() above already priced state.dispenser
      // - including picking a DIFFERENT offer code, since offerCode() takes
      // dispenser-included as an argument - so every figure on this card, the
      // dispenser line, the "included free / included, $77" sub-line and the
      // offer row have already followed. Only the label is left to mirror.
      /* "Future deliveries" (2026-08-07, Brian: "add back the section that show
       * cost for future deliveries like we have on the published theme").
       * Restored from E10's .aw-rev-card--future, same heading, same row, same
       * three bullets in the same order. Every figure comes from renewalCents()
       * - the shared expression the trial notice uses - so this block can never
       * quote a recurring price the notice or the cart drawer disagrees with,
       * and it re-prices on every quantity and cadence change like the rest of
       * the card. Nothing here is hardcoded. */
      const futEl = planEl.querySelector('[data-awz-future]');
      if (futEl) {
        const cadenceCap = cadence.charAt(0).toUpperCase() + cadence.slice(1);
        pout('renew-cadence').textContent = trial ? cadenceCap + ', ' + contMinQty() + ' jug plan' : cadenceCap;
        pout('renew-price').textContent = usd(renewalCents(q, reg, fee, trial));
        // "Free delivery" is a claim, not decoration: a surcharge ZIP pays a
        // per-delivery fee, so in that state the bullet states the fee instead
        // of asserting free delivery a few pixels under a fee line that
        // contradicts it. fee is 0 in every non-surcharge state.
        const delLi = futEl.querySelector('[data-awz-row="delivery"]');
        if (delLi) {
          delLi.classList.toggle('awz-fut--fee', fee > 0);
          delLi.querySelector('[data-out="renew-delivery"]').textContent =
            fee > 0 ? usd(fee) + ' delivery fee' : 'Free delivery';
        }
      }
      const dispTog = planEl.querySelector('[data-awz-disp-toggle]');
      if (dispTog) {
        dispTog.textContent = state.dispenser
          ? 'Already have a dispenser? Remove it'
          : 'Add a glass dispenser';
      }
      if (planCta) {
        planCta.disabled = adding || regionOut;
        planCta.classList.toggle('aw-btn--needs-zip', needsZip || !!waitlisted);
        planCta.textContent = adding ? 'Adding...'
          : waitlisted ? 'Join the waitlist first'
          : needsZip ? 'Enter your ZIP to order'
          : regionOut ? 'Ordering is paused for your region'
          : 'Get my first delivery';
      }
    }

    // Task 7: the adjust panel. Same money truth as the card - every figure
    // comes from awzQuote (q) and the shared fee/discount locals, rewritten on
    // every render (so every chip change). Lookups are scoped to
    // [data-awz-adjust-panel]; the panel sits after the plan sub, so the
    // first-match out() helper above still resolves the earlier panels'
    // duplicate data-out names.
    const adjEl = $('[data-awz-adjust-panel]');
    if (adjEl) {
      const aout = (n) => adjEl.querySelector('[data-out="' + n + '"]');
      $$('.awz-qch').forEach((b) => {
        const on = Number(b.dataset.qty) === state.qty;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      // A resumed or bulk-picked quantity lives in the tail: reveal it so the
      // active chip is never hidden behind "More".
      if (state.qty > 8) {
        adjEl.querySelectorAll('.awz-chip--tail').forEach((b) => { b.hidden = false; });
        const more = adjEl.querySelector('[data-awz-qty-more]');
        if (more) { more.hidden = true; more.setAttribute('aria-expanded', 'true'); }
      }
      // setWeeks owns is-active on .awz-cch (with applyCadenceUI owning
      // visibility); mirror it onto aria-pressed here.
      $$('.awz-cch').forEach((b) => b.setAttribute('aria-pressed', b.classList.contains('is-active') ? 'true' : 'false'));
      /* 2026-08-08: jug size + total volume, live, directly under the quantity
       * chips. 2.5 gallons is the published jug size (it was previously stated
       * only in the "See all plans + pricing" table heading); the per-delivery
       * total is what actually answers "is this enough for me".
       * 2026-08-09: the plan card now carries the same line under its title, for
       * the customer who accepts the default 4 jugs and never opens this panel.
       * Not repetition - the two .aw-gf-sub panels are never on screen together -
       * and both read volumeText(), so they cannot disagree. Still deliberately
       * absent from the plan title, the hint and the line-item rows. */
      const volEl = aout('volume');
      if (volEl) {
        const vTxt = volumeText(state.qty);
        if (volEl.textContent !== vTxt) volEl.textContent = vTxt;
      }
      aout('recurring').textContent = usd(headlineCents(q, fee, trial));
      aout('cadence').textContent = trial ? 'first delivery' : cadence;
      paintTrialNote(aout('trial-note'), trial, reg, fee);
      aout('water-label').textContent = 'Water, ' + state.qty + (state.qty === 1 ? ' jug' : ' jugs');
      aout('water').textContent = usd(water);
      aout('deposit-line').textContent = usd(deposits);
      const adjDispFree = state.dispenser && foreignCode === null && code === 'home77';
      const adjDispRow = adjEl.querySelector('[data-awz-row="dispenser"]');
      if (adjDispRow) {
        adjDispRow.hidden = !state.dispenser;
        const wasEl = adjDispRow.querySelector('[data-out="disp-was"]');
        const freeEl = adjDispRow.querySelector('.awz-free');
        if (wasEl) { wasEl.textContent = usd(D.dispenserCents); wasEl.classList.toggle('awz-nostrike', !adjDispFree); }
        if (freeEl) freeEl.hidden = !adjDispFree;
      }
      const adjFeeRow = adjEl.querySelector('[data-awz-row="fee"]');
      if (adjFeeRow) {
        adjFeeRow.hidden = fee === 0;
        if (fee > 0) adjFeeRow.querySelector('[data-out="fee-line"]').textContent = usd(fee);
      }
      const adjOfferRow = adjEl.querySelector('[data-awz-row="offer"]');
      if (adjOfferRow) {
        const showOffer = discount > 0 && code !== 'home77';
        adjOfferRow.hidden = !showOffer;
        if (showOffer) {
          adjOfferRow.querySelector('[data-out="offer-name"]').textContent =
            code === 'starter22' ? 'Starter offer, 22% off' : 'Family offer, 33% off';
          adjOfferRow.querySelector('[data-out="offer-off"]').textContent = '-' + usd(discount);
        }
      }
      aout('today').textContent = usd(q.todayCents + fee);
    }

    // The extras step's running total is downstream of every plan figure above
    // (quantity, cadence, dispenser, offer, fee), so it re-renders here too -
    // not only when an extra is toggled.
    renderExtrasTotal();

    // Tier table + region note track the resolved region (2026-08-18).
    paintTierTable();
  }

  /* Extras running total (2026-08-06, final-QA D1). The extras step used to
   * show no number at all: the last figure a customer saw was the plan card's
   * "First delivery $236", and adding a $222 shelf plus a $33 orb took them to
   * a $491 charge with nothing on screen updating.
   *
   * Total = awzQuote(state).todayCents (the single money truth for the first
   * order - it owns the curve-vs-catalogue rule, the per-line offer allocation
   * and the foreign-code stand-down) + the surcharge fee the plan card already
   * adds + the checked extras at face value. Extras carry no deposit and are
   * outside the offer base, so they add exactly as /cart.js bills them. Each
   * card's hidden [data-gf-extra] checkbox carries data-cents alongside the
   * data-variant toCheckout() reads, and a colour <select> rewrites BOTH, so
   * the figure can never belong to a different variant than the one that will
   * be added. */
  function extrasCents() {
    return $$('#aw-gf-step4 [data-gf-extra]')
      .filter((b) => b.checked)
      .reduce((sum, b) => sum + (Number(b.dataset.cents) || 0), 0);
  }

  function renderExtrasTotal() {
    const box = $('[data-awz-xtot]');
    if (!box) return;
    const E = state.eligibility;
    const fee = E && E.state === 'surcharge' ? E.fee * 100 : 0;
    const x = extrasCents();
    const row = box.querySelector('[data-awz-xrow-extras]');
    if (row) row.hidden = x === 0;
    const xa = box.querySelector('[data-out="x-extras"]');
    if (xa) xa.textContent = usd(x);
    const tot = box.querySelector('[data-out="x-total"]');
    /* While a re-sync confirmation is pending, the figure on screen IS the cart's
       own total - the customer has been asked to confirm that number, so a
       background repaint (a cart announce, an offer write) must not quietly put
       the old computed figure back. It gets cleared the moment the customer
       actually changes something: see clearConfirm(). */
    if (tot) tot.textContent = usd(confirmTotalCents != null ? confirmTotalCents : awzQuote(state).todayCents + fee + x);
  }

  /* Any deliberate change to what is being bought invalidates a pending
     confirmation - the customer must never confirm a figure that no longer
     describes their order. */
  function clearConfirm() { confirmTotalCents = null; }

  function setWeeks(weeks, planIdOverride) {
    clearConfirm();
    const rw = routeWeeks();
    if (rw && !planIdOverride && Number(weeks) % rw !== 0) weeks = rw;
    state.weeks = Number(weeks);
    const plan = planIdOverride ? { id: planIdOverride } : planForWeeks(state.weeks);
    state.planId = plan ? plan.id : null;
    state.planOv = !!planIdOverride;
    $$('.awz-cch').forEach((b) => b.classList.toggle('is-active', Number(b.dataset.weeks) === state.weeks && !planIdOverride));
    save();
    render();
  }

  function setQty(q) {
    clearConfirm();
    state.qty = Number(q);
    if (isTrialQty(state.qty)) state.household = 'trial';
    else if (state.household === 'trial') state.household = null;
    save();
    render();
  }

  // --- Wiring ---

  document.addEventListener('aw:eligibility', (e) => {
    const wasOrderable = canOrder();
    state.eligibility = e.detail;
    applyCadenceUI();
    /* 2026-08-08: step 1 is deleted, so a cleared verdict has nowhere to send
     * anyone. The customer stays on the plan card, which renders its own no-ZIP
     * state ("Enter your ZIP to order"), while render() drops .aw-flow-active
     * and re-expands the one ZIP gate on the page above them. A customer who had
     * already moved past the plan card is brought back to it, because neither
     * Extras nor Checkout is reachable without a verdict. */
    if (!e.detail) {
      if (state.step > 3) { goTo(3); return; }
      render();
      return;
    }
    /* A verdict that has just BECOME orderable used to be announced by the
     * step 1 -> step 3 transition inside goTo(). The customer now starts on
     * step 3, so goTo(3) would no-op on its `same` guard and the plan heading
     * would never take focus - the gate deliberately does not scroll on a
     * served result because "the guided flow owns this scroll".
     * So do the announce + focus explicitly, and ONLY on the transition: a
     * returning visitor's boot restore re-dispatches the verdict it booted with,
     * and must never yank the page. */
    const becameOrderable = !wasOrderable && canOrder();
    render();
    if (becameOrderable) {
      /* MEASUREMENT PARITY, deliberate. Before step 1 was deleted this exact
       * moment - a visitor supplying a ZIP the store serves - produced the
       * step 1 -> step 3 transition inside goTo(), and with it one gf_step_view
       * { step: 3, zone: 'plan' }. goTo(3) now no-ops on its `same` guard, so
       * without this line the plan-step view count would collapse to near zero
       * and the funnel would stop being comparable with the pre-change baseline.
       * The payload is byte-identical to goTo()'s, fires once per transition
       * (the !wasOrderable guard), and stays silent on a returning visitor's
       * boot restore, which re-dispatches the verdict it booted with. */
      try { if (typeof window.awTrackGate === 'function') window.awTrackGate('gf_step_view', { gate: 'guided_flow', step: 3, zone: gfZone(3) }); } catch (_) {}
      const h = document.querySelector('#aw-gf-step3 .aw-gf__h');
      announce('Step ' + DISPLAY_STEP[3] + ' of ' + DISPLAY_TOTAL + ': ' + (h ? h.textContent : ''));
      if (h) h.focus();
    }
  });

  $$('[data-gf-back]').forEach((btn) => {
    btn.addEventListener('click', () => goTo(Number(btn.dataset.gfBack)));
  });

  /* 2026-08-08: the delivery-ZIP context bar's Change control, and the rail's
   * backward navigation. Together they replace step 1 and the rail's Area step.
   *
   * Change CLICKS #aw-zip-change rather than reimplementing it. That button's
   * handler is the single tested implementation of "forget this verdict": it
   * drops the localStorage cache AND clears the five Delivery* cart attributes
   * through clearEligibility(), which is precisely the half-clear that shipped
   * broken once before (a customer changed ZIP and still checked out against the
   * old verdict). It is display:none while .aw-flow-active is on, which does not
   * matter - .click() fires a handler on a hidden element.
   *
   * No scroll or focus call here on purpose: clearEligibility() dispatches
   * aw:eligibility synchronously, the listener above re-renders and strips
   * .aw-flow-active, so by the time #aw-zip-change's own handler reaches
   * input.focus() the hero ZIP field is visible again and the browser scrolls it
   * into view itself. A second scroll here would fight that one. */
  const zipChangeBtn = $('[data-gf-zip-change]');
  if (zipChangeBtn) {
    zipChangeBtn.addEventListener('click', () => {
      const changeBtn = document.getElementById('aw-zip-change');
      if (changeBtn) changeBtn.click();
    });
  }

  /* Rail backward navigation. Only steps the customer has COMPLETED are
   * navigable; markDot() owns which those are and marks the rest aria-disabled.
   * The guard is re-checked here rather than trusted from the DOM alone, and a
   * forward target is refused outright, so a stale attribute can never let
   * someone jump to Checkout before choosing a plan. goTo() supplies the
   * gf_step_view event, the aria announcement and the focus move, exactly as a
   * forward transition does - the raw step ids in the payload are unchanged. */
  $$('[data-gf-nav]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.getAttribute('aria-disabled') === 'true') return;
      const target = Number(btn.dataset.gfNav);
      if (!(target < state.step)) return;
      goTo(target);
    });
  });

  // 2026-08-06: the dispenser opt-out on the plan card. It flips the ONE
  // existing state field rather than introducing a parallel flag, so a single
  // render() call re-prices the whole card through awzQuote() - and because
  // offerCode(qty, hasDispenser) reads the same flag, the OFFER ITSELF can
  // change when the dispenser goes (6 jugs: family33 applies with it, no offer
  // and the volume curve without it). doAdd() reads it when it builds the cart
  // items, and applyOffer() reads it when it picks the code to attach, so the
  // display, the cart and the discount can never disagree. save() persists it
  // across a reload like every other plan choice.
  const dispToggleBtn = $('[data-awz-disp-toggle]');
  if (dispToggleBtn) {
    dispToggleBtn.addEventListener('click', () => {
      state.dispenser = !state.dispenser;
      save();
      render();
    });
  }

  // Task 7: adjust-panel chips (Task 9 deleted the legacy water-panel buttons
  // and the More-schedules select that shared these paths). Cadence chips route
  // through setWeeks (applyCadenceUI owns their visibility); quantity chips
  // route through setQty so the trial household rule (qty <= 3) holds. "More"
  // reveals the bulk tail (10-32) - real bulk orders exist, so it stays.
  $$('.awz-cch').forEach((b) => b.addEventListener('click', () => setWeeks(b.dataset.weeks)));
  $$('.awz-qch').forEach((b) => b.addEventListener('click', () => setQty(Number(b.dataset.qty))));
  const qtyMoreBtn = $('[data-awz-qty-more]');
  if (qtyMoreBtn) qtyMoreBtn.addEventListener('click', () => {
    $$('.awz-chip--tail').forEach((b) => { b.hidden = false; });
    qtyMoreBtn.setAttribute('aria-expanded', 'true');
    qtyMoreBtn.hidden = true;
  });
  // Chip visibility (no-plan chips + cadence multiples) lives in applyCadenceUI.

  /* 2026-08-09, Brian: "if i click on see all pricing plans, and click a higher
   * number ie 28, the screen doesn't adjust back up to the plan". The tier table
   * and both quantity pickers live inside a <details> BELOW the whole wizard, so
   * the choice is made roughly 1,800px under the plan card it updates. goTo(3)
   * does move the caret to the step heading, but only on a real step CHANGE -
   * its `!same` guard - and by then the customer is already on step 3, so
   * nothing moved. Collapsing the <details> then shortened the page by ~1,040px
   * underneath a scroll position that stayed where it was, which pushed the
   * updated card further off screen rather than back into view. Measured before
   * this fix: at 375px, choosing 28 left step 3 ending 613px ABOVE the viewport
   * top; at 1280px, 407px above. The quantity applied correctly every time - it
   * was simply invisible, so the gesture read as "nothing happened".
   *
   * Called AFTER goTo, and synchronously: closing a <details> and re-rendering
   * the card both reflow immediately, so the geometry this reads is already the
   * post-collapse one. An earlier version deferred to requestAnimationFrame for
   * that settling and did not need to - and rAF does not fire in a backgrounded
   * tab, so the deferral only added a way for the scroll never to happen.
   *
   * behavior: 'instant' overrides the document's scroll-behavior: smooth on
   * purpose. This is a ~1,500px correction that ANSWERS a tap, so it should land
   * in the same frame as the collapse rather than animate for most of a second
   * over a page that no longer holds what the customer was looking at - and an
   * animated scroll is abandoned the moment they touch the screen, which on a
   * jump this long is likely. The other two scrollIntoView calls in this file
   * are short moves to the ZIP gate and deliberately keep the smooth default.
   * Verified after this change in Playwright WebKit at 393px and Chromium at
   * 1280px, on a visible page: all 13 controls in the table land step 3 at
   * scroll-padding-top (120px), i.e. rail, heading and card all on screen. */
  function revealPlanCard() {
    const step3 = document.getElementById('aw-gf-step3');
    if (step3 && !step3.hidden) step3.scrollIntoView({ block: 'start', behavior: 'instant' });
  }

  allEl.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-gf-choose]');
    if (!btn) return;
    const q = Number(btn.dataset.gfChoose);
    // Quantity-only controls (the trial and bulk pickers) change ONLY the
    // quantity. Variant B's quantity buttons have no side effects, so picking a
    // size must be the same gesture in both arms - forcing the dispenser on and
    // resetting the cadence here would hand the treatment a different offer mix
    // for an identical customer action. The tier-table "Choose this" rows keep
    // their original meaning: pick this whole recommended plan.
    state.picked = true;
    if (btn.dataset.gfQtyOnly !== undefined) {
      setQty(q);
      allEl.open = false;
      goTo(3);
      revealPlanCard();
      return;
    }
    state.qty = q;
    state.household = isTrialQty(q) ? 'trial' : null;
    // Brian, 2026-07-21: no dispenser re-force here either, see the step 2 handler.
    setWeeks(4);
    allEl.open = false;
    goTo(3);
    revealPlanCard();
  });

  /* 2026-08-06, Brian: "it is not actually filling up when you are adding your
   * plan and accessories". Root cause of the visible half: theme.js builds the
   * cart drawer's markup and the header cart bubble ONCE at page load, then only
   * ever refreshes them on its own 'cart:build' event (theme.CartForm listens for
   * it and re-fetches the cart section, which repaints the lines, the subtotal
   * and the count bubble). Every cart write in this file goes through fetch(), so
   * nothing ever told it. A customer who added a $236 plan and then tapped the
   * cart icon to check was shown "Your cart is currently empty" with no count on
   * the icon, while /cart.js held 9 items and $236.00. Announce every write. */
  function cartChanged() {
    AWCART.announce(); // coalesced: each dispatch costs us two app-driven cart reads
  }

  /* Cart reader with the same retry the add flow uses: a single Cloudflare 429
   * must not be read as "the cart does not contain this". */
  async function readCart(opts) {
    return AWCART.readSoft(opts);
  }

  /* Carries the Round 2 arm marker: one retry, still non-blocking. */
  async function writeAttrs(attrs) {
    await AWCART.post('/cart/update.js', { attributes: attrs });
  }

  // Add to cart: water + matched deposits + add-ons in one call, then
  // attributes (ZIP set + the Round 2 arm marker), then the offer code.
  // Task 6: one shared handler - the review panel's #aw-gf-add and the plan
  // card's [data-gf-submit] CTA both fire it (bound below).
  const doAdd = async () => {
    if (adding) return; // second click while the first add is in flight doubles the order
    const E = state.eligibility;
    if (!canOrder()) {
      /* 2026-08-08: this used to goTo(1), a panel whose only content was a
       * pointer back up the page. Step 1 is deleted, so the CTA now DOES the
       * thing it names: it takes the customer to the one ZIP gate on the page
       * and puts the caret in it. render() has already dropped .aw-flow-active
       * for anyone without a verdict, so the gate's input row is visible.
       * A waitlisted customer is sent to the waitlist field instead - their
       * answer is an email, not another ZIP. */
      if (E && E.state === 'waitlist') {
        const wl = document.getElementById('aw-zip-waitlist');
        const wlEmail = document.getElementById('aw-zip-email');
        if (wl && !wl.hidden) wl.scrollIntoView({ block: 'center' });
        if (wlEmail) wlEmail.focus({ preventScroll: true });
        return;
      }
      const zipInput = document.getElementById('aw-zip-input');
      if (zipInput) { zipInput.scrollIntoView({ block: 'center' }); zipInput.focus({ preventScroll: true }); }
      return;
    }
    if (region().available === false) return; // CTA is disabled; belt and braces
    const err = out('error');
    err.hidden = true;
    adding = true;
    let failure = null; // carries the classified cart result into the message below
    render(); // planCta picks up the in-flight disabled state + 'Adding...' label
    try {
      /* A repeat tap must not race the PREVIOUS add's settle work (2026-08-07).
       * `adding` is cleared as soon as the add lands, but postAddPending is still
       * writing the cart attributes and then applyOffer()'s discount code. A
       * second doAdd starting inside that window re-SETS the cart (zero, re-add)
       * underneath the in-flight offer write, and because the code is chosen from
       * the cart COMPOSITION it can end up detached entirely. Measured: 1 of 6
       * rapid double taps finished with /cart.js at 31300 while the card quoted
       * $236 - the free dispenser billed at $77. A displayed total BELOW the
       * charge is the one failure this page must never produce, so the whole
       * add-and-settle is one critical section. The writes are already serialised
       * by the transport queue; this orders the DECISIONS, not just the writes. */
      if (postAddPending) { try { await postAddPending; } catch (_) {} postAddPending = null; }
      if (!state.planId) { // broken restore or renamed plans: never post a one-time order
        const fallback = planForWeeks(4) || D.plans[0];
        if (fallback) state.planId = fallback.id;
      }
      // "Start my delivery" SETS the cart to the plan: zero any existing
      // plan lines first, so going back to adjust (4 -> 6) then re-adding
      // replaces the order instead of stacking a second one on top - and the
      // offer code below is applied against an unambiguous composition.
      // Other products in the cart are left alone.
      // planVariantIds() is shared with the checkout guard's knownVariantIds()
      // so the two can never drift about which lines this wizard owns. Same
      // list, same order, same String() coercion as before.
      const planVariants = planVariantIds();
      // Fresh by contract: this read decides what gets zeroed, so it must never
      // be served from the shared cache. The transport retries a rate-limited
      // read ONCE with real backoff instead of three times inside the same block.
      let existing = null;
      try {
        existing = await AWCART.read({ fresh: true });
      } catch (e) {
        failure = { transient: e && e.transient !== false, status: (e && e.status) || 0 };
      }
      if (!existing) throw new Error('cart unreadable'); // abort beats a silently doubled order
      for (const line of existing.items) {
        if (planVariants.indexOf(String(line.variant_id)) === -1) continue;
        // A swallowed zeroing failure followed by the add DOUBLES the order
        // (observed under Cloudflare 429 bursts, incl. 2026-07-19): retry
        // once, then abort to the honest retry error like the guard read.
        // /cart/change.js sets an ABSOLUTE quantity, so it is idempotent and the
        // transport may safely retry it once. Serialised behind the write queue,
        // so it can never overlap the add below or an extras write.
        let zr = await AWCART.post('/cart/change.js', { id: line.key, quantity: 0 });
        // A STALE LINE KEY is not a failed operation (2026-08-07). /cart/change.js
        // answers 400 when the key no longer identifies a line, and Shopify RE-KEYS
        // a line whenever its discount allocation changes - which is exactly what
        // applyOffer()'s {discount:'home77'} write does, in the same window as this
        // loop on a fast repeat tap. Observed live: add 200, attrs 200, discount 200,
        // then this zero 400 on the dispenser's pre-discount key, which aborted the
        // whole add and printed a failure over a cart that was already correct.
        // Re-read, and judge by the END STATE we actually want (that variant absent)
        // rather than by the status of one write.
        if (!zr.ok) {
          const fresh = await AWCART.readSoft({ fresh: true });
          if (!fresh) { failure = zr; throw new Error('cart zero unverifiable ' + zr.status); }
          const again = fresh.items.find((l) => String(l.variant_id) === String(line.variant_id));
          if (again) {
            zr = await AWCART.post('/cart/change.js', { id: again.key, quantity: 0 });
            if (!zr.ok) { failure = zr; throw new Error('cart zero failed ' + zr.status); }
          }
          // no line for that variant any more: the goal is already met, carry on
        }
      }
      const items = [
        { id: region().id, quantity: state.qty, selling_plan: state.planId },
        { id: D.depositVariantId, quantity: state.qty },
      ];
      if (state.dispenser) items.push({ id: D.dispenserVariantId, quantity: 1 });
      if (state.shelf) items.push({ id: D.shelfVariantId, quantity: 1 });
      if (state.stand && D.standVariantId) items.push({ id: D.standVariantId, quantity: 1 });
      if (state.spigot && D.spigotVariantId) items.push({ id: D.spigotVariantId, quantity: 1 });
      if (state.shelfWhite && D.shelfWhiteVariantId) items.push({ id: D.shelfWhiteVariantId, quantity: 1 });

      // NEVER blind-retried. addItems settles, re-reads, and re-sends only if the
      // cart proves the add did not land, so a lost response cannot double the
      // order the way a swallowed 429 did on 2026-07-19.
      const r = await AWCART.addItems(items, existing);
      if (!r.ok) { failure = r; throw new Error('add failed ' + r.status); }
      cartChanged(); // drawer lines + subtotal + header cart count follow the write

      // Affiliate ref rides the same write as the ZIP set and the arm marker, so
      // an affiliate link is credited even when the customer never uses that
      // affiliate's coupon (aw-affiliate-ref.js). Attribute NAMES must match the
      // control arm exactly or the experiment readout is confounded.
      // Perceived-speed fix (Brian 2026-07-29): show the extras step the moment
      // the add lands. Attribute + offer writes never throw (both retry and
      // swallow internally, exactly as when they were awaited here) - the only
      // hard requirement is that they finish before checkout, which toCheckout
      // guarantees by awaiting postAddPending.
      goTo(4);
      // doAdd re-SETS the order: it zeroes the accessory variants it owns before
      // adding, so the extras cards must be repainted from the cart or a customer
      // who goes back to the plan card and re-adds lands on step 4 with cards
      // still saying "Added" for lines that no longer exist. Non-blocking.
      syncExtrasFromCart();
      // postAddBusy, not postAddPending, is what "a write of ours is still in
      // flight" means: postAddPending is never reset to null after an add (only
      // toCheckout clears it), so anything that tested it for truthiness would
      // stay blocked for the rest of the page view. Found the hard way by the
      // external-cart listener at the bottom of init(), 2026-08-07.
      postAddBusy = true;
      postAddPending = (async () => {
        try {
          await writeAttrs(Object.assign(
            (typeof window.awAffiliateAttrs === 'function') ? window.awAffiliateAttrs() : {},
            {
              'Delivery ZIP': E.zip,
              'Delivery Eligibility': E.state,
              'Delivery Fee': E.fee == null ? '' : String(E.fee),
              'Delivery Cadence': E.cadence ? 'once every ' + E.cadence.weeks + ' weeks (' + E.cadence.label + ')' : '',
              'AW Theme Variant': 'E2',
            }
          ));
          await applyOffer();
        } finally { postAddBusy = false; }
      })().catch(() => {});
    } catch (e) {
      err.hidden = false;
      // Honest and actionable: a rate-limited cart (retry works) must not read
      // the same as a rejected variant (retry never works).
      err.textContent = AWCART.message(failure || { unknown: true }, 'Get my first delivery');
    } finally {
      adding = false;
      // render() owns the button's disabled state and label. Belt and braces: if
      // it ever throws, restore the CTA rather than stranding a dead button.
      try { render(); } catch (_) {
        if (planCta) { planCta.disabled = false; planCta.textContent = 'Get my first delivery'; }
      }
    }
  };
  if (planCta) planCta.addEventListener('click', doAdd);

  /* Auto-apply the first-order discount. C fork of B's mechanism: the cart
   * API's discount field REPLACES the cart's whole code set. B's
   * /discount/{code} route only APPENDS - Shopify then keeps whichever
   * attached code saves the customer most, which is how home77 (-$77) beat
   * family33 (-$74.91) on a 6 jug cart (Brian's 2026-07-18 report; same
   * mechanism as Round 1 finding 6). aw-cart-sync (C fork) re-asserts on
   * cart edits via aw_offer_code. */
  async function applyOffer() {
    if (!D.offersLive) return;
    // Returning customer: none of the three codes can be redeemed, so attaching
    // one only produces a cart total checkout refuses to honour. Write nothing
    // (which also leaves any code the customer brought with them untouched) and
    // clear aw_offer_code, so aw-cart-sync cannot re-assert the code left armed
    // by their FIRST order the next time quantities change (Brian, 2026-07-21).
    // aw-cart-sync ships on the drawer with no liquid data blob, so it cannot
    // read customerEligible; the durable marker is how it learns to stand down.
    // The eligible path clears the marker again, so a logout or a different
    // customer in the same browser is not permanently suppressed.
    if (!D.customerEligible) {
      try { localStorage.removeItem('aw_offer_code'); localStorage.setItem('aw_offer_ineligible', '1'); } catch (_) {}
      /* 2026-08-09 hotfix (Brian, hard-blocked at checkout on his own phone).
       * Writing NOTHING here was only ever safe against a code the customer
       * BROUGHT. It was wrong against a theme code THIS BROWSER armed earlier:
       * a guest session applies home77, the same person then signs in,
       * orders_count > 0 makes them ineligible, awzQuote drops the offer and
       * quotes FULL price - but the cart still carries the discount. The
       * checkout guard then compared 31300 against 23600, and its repair pass
       * calls this very function, which returned right here without touching
       * the cart, so it could NEVER converge. The customer was blocked from
       * checking out permanently, in that browser, with no way to clear it.
       * Detach OUR codes only. A returning customer cannot redeem them anyway
       * (checkout refuses to honour that total), so removing them simply makes
       * the cart agree with the figure the summary has been quoting all along.
       * A code we do not own is still left completely untouched - and is now
       * NAMED, so the guard below can recognise and allow it. */
      try {
        const cur = await AWCART.readSoft({ fresh: true });
        if (cur) {
          const codes = cur.discount_codes || [];
          const theirs = codes.find((d) => THEME_CODES.indexOf(String(d.code || '').toLowerCase()) === -1);
          const ours = codes.some((d) => THEME_CODES.indexOf(String(d.code || '').toLowerCase()) !== -1);
          foreignCode = theirs ? String(theirs.code || '') : null;
          if (ours && !theirs) {
            const r = await AWCART.post('/cart/update.js', { discount: '' });
            if (!r.ok) clearDiscountCookie();
            cartChanged();
          }
          try { render(); } catch (_) {}
        }
      } catch (_) {}
      return;
    }
    try { localStorage.removeItem('aw_offer_ineligible'); } catch (_) {}
    const code = offerCode(state.qty, state.dispenser);
    try {
      // Never overwrite a code we did not put there. A customer who arrived on an
      // ambassador/affiliate link (or typed their own promo) keeps it; we simply
      // do not auto-apply ours. Fail CLOSED on an unreadable cart: guessing wrong
      // under a 429 burst would re-clobber the referral code this guard protects.
      // Retried like the add flow's cart read (Brian, 2026-07-21): a single 429
      // used to return here BEFORE arming aw_offer_code, so no code was attached
      // and aw-cart-sync, which is gated on that key, could never heal it.
      // Shared reader: this used to be three more /cart.js hits landing on top of
      // the add's own burst, and it produced 3 of the 429s in the captured trace.
      const cur = await AWCART.readSoft({ fresh: true });
      // We still fail closed on the write, but arm aw-cart-sync rather than
      // leave it disarmed: its next reconcile pass can re-assert the code behind
      // its own THEME_CODES guard, so a foreign code is still safe.
      if (!cur) {
        localStorage.setItem('aw_offer_code', code || 'none');
        return;
      }
      // Name the winning code when the cart gives it, so render() can say which
      // one is being used instead of promising a discount we never write.
      const foreign = (cur.discount_codes || []).find((d) => THEME_CODES.indexOf(String(d.code || '').toLowerCase()) === -1);
      if (foreign) {
        foreignCode = String(foreign.code || '');
        try { localStorage.removeItem('aw_offer_code'); } catch (_) {}
        /* 2026-08-09: re-quote. Discovering a foreign code CHANGES awzQuote's
         * answer (it forces code = null), so the summary has to repaint. Without
         * this it kept promising our offer while the cart billed the customer's
         * own code, and the checkout guard was handed two different numbers. */
        try { render(); } catch (_) {}
        return;
      }
      foreignCode = null; // the cart's codes are ours again: the offer is live
      localStorage.setItem('aw_offer_code', code || 'none');
      // code null writes discount:'' on purpose: that is the only path that
      // DETACHES a theme code the composition no longer qualifies for, so the
      // cart never carries a discount the summary stopped quoting. Safe because
      // the foreign-code check above already returned on any cart carrying a
      // code we do not own. Revert 2026-07-21.
      const r = await AWCART.post('/cart/update.js', { discount: code || '' });
      if (!r.ok) { // legacy fallback: cookie route
        if (code) await fetch('/discount/' + code);
        else clearDiscountCookie();
      }
      /* 2026-08-07 (side-cart removal defect). This write RE-KEYS every line the
       * discount allocates to, and until now nothing told the drawer. theme.js
       * had already rendered its rows from the pre-discount cart, so their
       * data-key values were dead on arrival and any removal in the side cart
       * came back 400 "no valid id or line parameter" - see guardStaleLineKey()
       * in aw-cart-sync.js for the captured trace. Announcing here re-renders
       * the drawer against the live cart, with live keys and the discounted line
       * prices. announce() is trailing-edge coalesced at 450ms, so this costs at
       * most one extra cart:build per add, not one per write. */
      cartChanged();
    } catch (_) { /* non-blocking; checkout minimums still guard the codes */ }
  }

  /* E2 (2026-07-28, Thomas package): the wizard never opens the side drawer.
   * Step 4 offers one-time extras; both CTAs land on /checkout. Cart
   * attributes (affiliate ref, ZIP, marker) were written by the add flow and
   * survive the redirect. Re-reads the cart and adds only missing variants so
   * a back-and-forth through this step never doubles an accessory. */
  const checkoutBtn = $('#aw-gf-checkout');
  const skipBtn = $('#aw-gf-skip');
  /* 2026-08-06, Brian (mobile): "in some instances, it is not actually filling up
   * when you are adding your plan and accessories."
   *
   * SECOND ROOT CAUSE. Until now the Add button only TOGGLED the card's hidden
   * [data-gf-extra] checkbox, relabelled itself "Added" and moved the running
   * total. Nothing reached /cart/add.js: toCheckout() batch-added the checked
   * variants at the very last moment, on the way to /checkout. Measured on E8 at
   * 393px: tapping Add on the Wood Counter Stand moved the on-screen total
   * $236 -> $280 while /cart.js stayed at 9 items / $236.00. A customer who then
   * opened the cart to check saw no stand in it, and reasonably concluded the
   * add had failed - the button was claiming something that was not true.
   *
   * Add now writes to the cart immediately and Add-again ("Added" -> tap) takes
   * the line back out, so the label, the running total, the header cart count
   * and /cart.js move together at every step. The card is repainted FROM the
   * cart after every write (syncExtrasFromCart), never from local intent, so a
   * failed write can never leave the button claiming "Added".
   *
   * toCheckout()'s batch pass is deliberately kept: it adds only variants that
   * are missing from the cart, so it is now an idempotent safety net rather than
   * the add path - if an inline write ever fails, the sale is still not lost. */
  let xBusy = false;

  /* Which cart line, if any, belongs to this card (2026-08-07, pre-launch audit
   * CRITICAL-1).
   *
   * THE DEFECT. syncExtrasFromCart() matched the cart against xVariant(card),
   * which on a server-rendered page is the card's DEFAULT option. A customer who
   * picked Blue on the Orb Bag or White on the Steel Shelf and tapped Add got a
   * correct running total - and then, on any reload or return to the page, a card
   * repainted "Add", a dropdown snapped back to Gray, and a total that had
   * dropped by the price of a line still sitting in their cart. Measured: page
   * "Order total $236" over a Check out button while /cart.js held 33200, and one
   * reproduction reached a checkout charging $444.00 against a page reading $348.
   * Maximum single-session under-quote $318 (Steel Shelf $222 + Orb Bag $96).
   *
   * It also fed a second, quieter fault: toCheckout() adds every checked box's
   * data-variant that is missing from the cart, so a card stranded on its default
   * would have added the DEFAULT colour on top of the chosen one at checkout.
   *
   * THE MATCH, widest correct thing first:
   *   1. the exact variant the card currently carries (steady state, no change);
   *   2. otherwise ANY line of the SAME PRODUCT. The five cards are five distinct
   *      products (wood-counter-stand, steel-shelf, 1-litre-orb-bottle,
   *      waterbottleholder, copper-spigots), so a product-id match cannot make two
   *      cards claim one line, and it catches every colour, every size this card
   *      filters out of its dropdown, and every variant that has since gone
   *      unavailable or draft - all of which the customer is being CHARGED for and
   *      none of which may be silently dropped from the total.
   * data-awz-xproduct is server-rendered on each card in aw-guided-flow.liquid. */
  function xMatchLine(card, items) {
    const box = card.querySelector('[data-gf-extra]');
    const cur = box ? Number(box.dataset.variant) : 0;
    let line = cur ? items.find((i) => Number(i.variant_id) === cur) : null;
    if (line) return line;
    const pid = Number(card.dataset.awzXproduct) || 0;
    if (!pid) return null;
    return items.find((i) => Number(i.product_id) === pid) || null;
  }

  /* Move the card ONTO the variant the cart actually holds: the hidden checkbox's
   * data-variant (what toCheckout reads), its data-cents (what the running total
   * sums), the dropdown selection, the price and the photo. Money comes from the
   * cart line, never from the catalogue - final_line_price is exactly what
   * /cart.js bills for that line, including a quantity above 1, so extrasCents()
   * can no longer disagree with the charge. */
  function xAdopt(card, line) {
    const box = card.querySelector('[data-gf-extra]');
    if (!box) return;
    const vid = Number(line.variant_id);
    const lineCents = Number(line.final_line_price);
    box.dataset.variant = String(vid);
    if (isFinite(lineCents)) box.dataset.cents = String(lineCents);
    const sel = card.querySelector('[data-awz-xselect]');
    if (!sel) return;
    let opt = null;
    for (let i = 0; i < sel.options.length; i++) {
      if (Number(sel.options[i].value) === vid) { opt = sel.options[i]; break; }
    }
    if (!opt) {
      /* The cart holds a variant this dropdown no longer offers (sold out since
       * the page rendered, drafted, or a size this card filters out). It is being
       * charged either way, so it stays in the total AND it gets printed - a
       * dropdown that silently snapped back to Gray is how this defect looked to
       * a customer in the first place. */
      opt = document.createElement('option');
      opt.value = String(vid);
      opt.textContent = line.variant_title || line.product_title || 'Selected';
      opt.dataset.label = line.variant_title || '';
      if (isFinite(Number(line.final_price))) {
        opt.dataset.cents = String(Number(line.final_price));
        opt.dataset.price = usd(Number(line.final_price));
      }
      sel.appendChild(opt);
    }
    sel.value = String(vid);
    const priceEl = card.querySelector('[data-awz-xprice]');
    if (priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;
    const img = card.querySelector('[data-awz-ximg]');
    if (img && opt.dataset.thumb) {
      img.src = opt.dataset.thumb;
      if (opt.dataset.thumb2x) img.srcset = opt.dataset.thumb + ' 320w, ' + opt.dataset.thumb2x + ' 640w';
    }
    const btn = card.querySelector('[data-awz-xadd]');
    const nm = card.dataset.awzXname;
    if (btn && nm) {
      btn.setAttribute('aria-label', 'Add the ' + nm + (opt.dataset.label ? ' in ' + opt.dataset.label : '') + ' to your order');
    }
  }

  function xPaint(card, on) {
    clearConfirm(); // adding or removing an accessory changes the total
    const box = card.querySelector('[data-gf-extra]');
    const btn = card.querySelector('[data-awz-xadd]');
    if (box) box.checked = on;
    if (btn) {
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.textContent = on ? 'Added' : 'Add';
      btn.classList.toggle('is-added', on);
    }
    card.classList.toggle('is-picked', on);
  }

  function xError(msg) {
    const err = out('error4');
    if (!err) return;
    err.hidden = !msg;
    if (msg) err.textContent = msg;
  }

  /* The cart is the only truth for what a card shows. Called after every extras
   * write, after the plan add (doAdd re-SETS the order and zeroes the stand /
   * shelf / spigot lines, so the cards have to follow it down) and on boot (a
   * resumed session must never boot showing "Added" for something the cart no
   * longer holds - the open Task 6 minor). */
  let lastExtrasSyncAt = 0; // read by the external cart:build listener below
  async function syncExtrasFromCart(cart) {
    if (!$('[data-awz-xcard]')) return null;
    const c = cart || await readCart();
    if (!c) return null;
    const items = c.items || [];
    $$('[data-awz-xcard]').forEach((card) => {
      const line = xMatchLine(card, items);
      if (line) xAdopt(card, line); // restore the CHOSEN variant, not the default
      xPaint(card, !!line);
    });
    renderExtrasTotal();
    lastExtrasSyncAt = Date.now();
    return c;
  }

  document.addEventListener('click', function (e) {
    const btn = e.target && e.target.closest && e.target.closest('[data-awz-xadd]');
    if (!btn) return;
    const card = btn.closest('[data-awz-xcard]');
    const box = card && card.querySelector('[data-gf-extra]');
    if (!box) return;
    if (xBusy) return; // one cart write at a time; a double tap must never double-add
    const variant = Number(box.dataset.variant);
    if (!variant) return;
    const wantOn = !box.checked;
    xBusy = true;
    xError('');
    btn.disabled = true;
    btn.textContent = wantOn ? 'Adding...' : 'Removing...';
    (async () => {
      let failure = null;
      try {
        if (wantOn) {
          // Not blind-retried: see addItems. The card is painted FROM the cart,
          // so "already present" is a true recovery, not a guess.
          const r = await AWCART.addItems([{ id: variant, quantity: 1 }], null);
          if (!r.ok) { failure = r; throw new Error('extra add failed ' + r.status); }
        } else {
          const c = await AWCART.read({ fresh: true });
          const line = c.items.find((i) => Number(i.variant_id) === variant);
          if (line) {
            const r = await AWCART.post('/cart/change.js', { id: line.key, quantity: 0 });
            if (!r.ok) { failure = r; throw new Error('extra remove failed ' + r.status); }
          }
        }
        await syncExtrasFromCart();
        cartChanged();
      } catch (e) {
        if (!failure) failure = { transient: !!(e && e.transient), status: (e && e.status) || 0 };
        xError(AWCART.message(failure, wantOn ? 'Add' : 'Remove'));
        await syncExtrasFromCart(); // repaint from the cart, never from intent
      } finally {
        btn.disabled = false;
        // syncExtrasFromCart normally rewrites the label; restore it if the cart
        // read itself failed, rather than stranding "Adding...".
        if (btn.textContent === 'Adding...' || btn.textContent === 'Removing...') {
          btn.textContent = box.checked ? 'Added' : 'Add';
        }
        xBusy = false;
      }
    })();
  });

  // Task 9 (2026-08-05): colour selects on the Steel Shelf and Orb Bag cards.
  // The hidden [data-gf-extra] checkbox's data-variant is the ONLY thing toCheckout()
  // reads, and it reads it at CHECKOUT time - so rewriting the attribute here is the
  // whole mechanism, no per-card add-to-cart needed. Price and photo follow the option
  // so a card can never show a price belonging to a different variant.
  document.addEventListener('change', function (e) {
    const sel = e.target && e.target.closest && e.target.closest('[data-awz-xselect]');
    if (!sel) return;
    const card = sel.closest('[data-awz-xcard]');
    if (!card) return;
    const opt = sel.options[sel.selectedIndex];
    if (!opt) return;
    const box = card.querySelector('[data-gf-extra]');
    // 2026-08-06: data-cents moves with data-variant, or a running total could
    // quote one colour's price while checkout charges another's.
    const prevVariant = box ? Number(box.dataset.variant) : 0;
    const wasInCart = !!(box && box.checked);
    if (box) {
      box.dataset.variant = sel.value;
      if (opt.dataset.cents) box.dataset.cents = opt.dataset.cents;
    }
    // 2026-08-06: now that Add writes to the cart immediately, recolouring a card
    // that is ALREADY in the cart has to move the cart line too - otherwise the
    // customer is charged the colour they first tapped while the card, the
    // running total and checkout all show the new one.
    if (wasInCart && prevVariant && prevVariant !== Number(sel.value) && !xBusy) {
      xBusy = true;
      (async () => {
        let failure = null;
        try {
          const c = await AWCART.read({ fresh: true });
          const line = c.items.find((i) => Number(i.variant_id) === prevVariant);
          if (line) {
            const r0 = await AWCART.post('/cart/change.js', { id: line.key, quantity: 0 });
            if (!r0.ok) { failure = r0; throw new Error('colour swap remove failed ' + r0.status); }
          }
          const r1 = await AWCART.addItems([{ id: Number(sel.value), quantity: 1 }], null);
          if (!r1.ok) { failure = r1; throw new Error('colour swap add failed ' + r1.status); }
          xError('');
          await syncExtrasFromCart();
          cartChanged();
        } catch (e) {
          if (!failure) failure = { transient: !!(e && e.transient), status: (e && e.status) || 0 };
          xError(AWCART.message(failure, 'Add'));
          await syncExtrasFromCart();
        } finally {
          xBusy = false;
        }
      })();
    }
    const priceEl = card.querySelector('[data-awz-xprice]');
    if (priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;
    const img = card.querySelector('[data-awz-ximg]');
    if (img && opt.dataset.thumb) {
      img.src = opt.dataset.thumb;
      if (opt.dataset.thumb2x) img.srcset = opt.dataset.thumb + ' 320w, ' + opt.dataset.thumb2x + ' 640w';
    }
    const btn = card.querySelector('[data-awz-xadd]');
    const nm = card.dataset.awzXname;
    if (btn && nm) {
      btn.setAttribute('aria-label', 'Add the ' + nm + (opt.dataset.label ? ' in ' + opt.dataset.label : '') + ' to your order');
    }
    renderExtrasTotal();
  });

  // Task 9: extras carousel. Operable with no horizontal drag - the arrow buttons
  // scroll one card at a time, and the track is a plain focusable scroll container so
  // arrow keys scroll it and tabbing to a card's Add button scrolls that card into view
  // natively. Nothing is focus-trapped and the arrows are never `disabled`, only
  // aria-disabled, so focus is never yanked off a button the user is standing on.
  (function () {
    const track = document.querySelector('[data-awz-xtrack]');
    if (!track) return;
    const dots = document.querySelector('[data-awz-xdots]');
    const navs = $$('[data-awz-xnav]');
    function cardStep() {
      const c = track.querySelector('.awz-xcard');
      return c ? c.getBoundingClientRect().width + 12 : Math.round(track.clientWidth / 2) || 1;
    }
    function sync() {
      const view = Math.max(1, track.clientWidth);
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const pages = Math.max(1, Math.ceil(track.scrollWidth / view));
      const idx = Math.min(pages - 1, Math.round(track.scrollLeft / view));
      if (dots) {
        if (dots.children.length !== pages) {
          dots.innerHTML = '';
          for (let i = 0; i < pages; i++) dots.appendChild(document.createElement('i'));
        }
        Array.prototype.forEach.call(dots.children, function (d, i) { d.classList.toggle('is-on', i === idx); });
      }
      navs.forEach(function (b) {
        const dir = Number(b.dataset.awzXnav);
        const spent = dir > 0 ? track.scrollLeft >= max - 2 : track.scrollLeft <= 2;
        b.setAttribute('aria-disabled', max > 1 && spent ? 'true' : 'false');
      });
    }
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    // Step 4 is `hidden` at boot, so the first sync() runs against clientWidth 0 and
    // draws a single dot. A ResizeObserver re-syncs the moment the track gets a real
    // width (i.e. when goTo(4) unhides the step) - measured: dots stuck at 1 without
    // this, correct 3 with it. Cheaper and less brittle than re-adding a goTo() hook.
    if (window.ResizeObserver) { try { new ResizeObserver(sync).observe(track); } catch (_) {} }
    document.addEventListener('click', function (e) {
      const b = e.target && e.target.closest && e.target.closest('[data-awz-xnav]');
      if (!b) return;
      track.scrollBy({ left: cardStep() * Number(b.dataset.awzXnav), behavior: 'smooth' });
    });
    sync();
  })();
  /* ==================== CHECKOUT TOTAL GUARD ==============================
   * CRITICAL-1, final pre-launch audit 2026-08-07.
   *
   * THE DEFECT THIS EXISTS TO KILL. applyOffer() fails closed on an unreadable
   * cart - `const cur = await AWCART.readSoft({fresh:true}); if (!cur) {...;
   * return; }` - so the discount is NEVER WRITTEN. It runs inside
   * postAddPending, which doAdd() fires WITHOUT awaiting. toCheckout() awaits
   * that promise, but awaiting a function that deliberately gave up is not a
   * check, and nothing between the quoted price and window.location.href =
   * '/checkout' ever compared the cart to the screen.
   *
   * REPRODUCED END TO END (393x852, default 4-jug plan, ZIP 90291): answer
   * /cart.js with 429 for 9s after the CTA and the page still reads $236 with
   * no error of any kind, while the Shopify checkout it navigates to asks for a
   * flat $313.00 with no discount line. Re-reproduced 3 of 3 before this fix.
   * The trigger is ordinary, not exotic - the store's own installed apps (LB
   * Selleasy, Appstle Bundles) generate exactly that read pressure and it
   * occurred naturally three times during the audit. EVERY first order on this
   * page carries a code, so every first order was exposed: $77 at 4 jugs,
   * $76.89 at 6, $28.38 on a 2-jug trial.
   *
   * THE RULE, and it has no exceptions: never navigate to checkout on a total
   * we have not verified. Read the authoritative cart through the SHARED
   * transport (AWCART) so the check inherits the dedupe, the write queue and
   * the one honest retry, rather than adding a fourth raw fetch to the very
   * burst that causes the problem. Compare it to the figure on screen. If they
   * disagree, re-apply the missing discount and re-verify. If it still
   * disagrees, or the cart cannot be read at all, STOP and say so.
   *
   * A failure the customer can see and retry is strictly better than a silent
   * overcharge - the same ruling already accepted for doAdd()'s "Your cart is
   * busy right now. Nothing has been charged." path. */

  const GUARD_REPAIRS = 2; // initial check + at most 2 repair rounds

  /* Every variant this wizard is responsible for. Shared with doAdd()'s zeroing
   * loop, which is the whole point: one definition of "ours". */
  function planVariantIds() {
    return [D.depositVariantId, D.dispenserVariantId, D.shelfVariantId]
      .concat(D.standVariantId ? [D.standVariantId] : [])
      .concat(D.spigotVariantId ? [D.spigotVariantId] : [])
      .concat(D.shelfWhiteVariantId ? [D.shelfWhiteVariantId] : [])
      .concat(Object.keys(D.regions).map((k) => D.regions[k].id)).map(String);
  }

  /* ...plus every extras variant the step can put in the cart. Both the checked
   * [data-gf-extra] and EVERY option of each colour <select>, because a card
   * whose dropdown has moved on still owns the line it added. */
  function knownVariantIds() {
    const ids = planVariantIds();
    $$('#aw-gf-step4 [data-gf-extra]').forEach((b) => {
      if (b.dataset.variant) ids.push(String(b.dataset.variant));
    });
    $$('#aw-gf-step4 [data-awz-xselect]').forEach((s) => {
      Array.prototype.forEach.call(s.options, (o) => { if (o.value) ids.push(String(o.value)); });
    });
    return ids;
  }

  function parseUsdCents(text) {
    if (text == null) return null;
    const digits = String(text).replace(/[^0-9.]/g, '');
    if (!/[0-9]/.test(digits)) return null;
    const n = Number(digits);
    return isFinite(n) ? Math.round(n * 100) : null;
  }

  /* The number the customer is actually looking at when they tap Check out or
   * Skip: step 4's "Order total" row. Read from the DOM rather than recomputed,
   * so a stale paint is caught too - whatever is drawn is what they acted on.
   * Falls back to the expression renderExtrasTotal() paints from only if the
   * node is missing or unparseable. */
  function displayedTotalCents() {
    const box = $('[data-awz-xtot]');
    const el = box && box.querySelector('[data-out="x-total"]');
    const shown = el ? parseUsdCents(el.textContent) : null;
    if (shown != null) return shown;
    const E = state.eligibility;
    const fee = E && E.state === 'surcharge' ? E.fee * 100 : 0;
    return awzQuote(state).todayCents + fee + extrasCents();
  }

  /* What the cart will charge for the lines WE quoted. total_price is the
   * authority; lines we do not own are subtracted at the price the cart itself
   * bills them. doAdd() deliberately leaves other products alone ("Other
   * products in the cart are left alone"), so a customer who added a compact
   * bottle elsewhere on the site carries a line our quote never claimed to
   * cover - without this subtraction every one of them would fail closed
   * forever, which would be a worse defect than the one being fixed. With no
   * foreign lines this is exactly total_price, i.e. a strict comparison. */
  function ourCartCents(cart) {
    const known = knownVariantIds();
    let foreign = 0;
    (cart.items || []).forEach((i) => {
      if (known.indexOf(String(i.variant_id)) === -1) foreign += Number(i.final_line_price) || 0;
    });
    return Number(cart.total_price) - foreign;
  }

  /* `want` is captured at the moment of the TAP and never recomputed: the
   * contract is that the cart matches the figure the customer clicked on, not
   * whatever the card happens to say a few seconds later. */
  async function verifyCheckoutTotal(want) {
    for (let attempt = 0; attempt <= GUARD_REPAIRS; attempt++) {
      const cart = await AWCART.readSoft({ fresh: true });
      const got = cart ? ourCartCents(cart) : null;
      if (cart && got === want) return { ok: true, want: want, got: got, repairs: attempt };
      /* 2026-08-09. A code the customer brought is deliberately never folded
       * into our quote (awzQuote forces code = null the moment foreignCode is
       * set), so the cart legitimately bills LESS than the figure on screen -
       * and strict equality blocked those customers outright. Reproduced live:
       * want 23600, got 18100 on a cart carrying `freedeposits`. Allow it, but
       * ONLY when the entire gap is that discount. The line composition is
       * still verified item for item, and the customer can only ever be charged
       * LESS than we quoted, never more. Every other gap still fails closed,
       * which is the whole point of this guard. */
      if (cart && foreignCode && got != null &&
          got + (Number(cart.total_discount) || 0) === want) {
        return { ok: true, want: want, got: got, foreign: foreignCode, repairs: attempt };
      }
      if (attempt === GUARD_REPAIRS) {
        return { ok: false, reason: cart ? 'mismatch' : 'unreadable', want: want, got: got, repairs: attempt };
      }
      /* Repair. applyOffer() is the write the 429 window skipped, and it is
       * idempotent: it re-reads, stands down on a foreign/ambassador code, and
       * posts the discount this composition earns. It touches nothing else -
       * no quantities, no deposits, no selling plan - so a repair can only ever
       * move the cart toward the quoted figure. */
      try { await applyOffer(); } catch (_) {}
      await new Promise((r) => setTimeout(r, 400));
    }
    return { ok: false, reason: 'mismatch', want: want, got: null, repairs: GUARD_REPAIRS };
  }

  let checkingOut = false;
  /* 2026-08-09 backstop. Holds the cart figure the customer has been SHOWN and
   * asked to confirm after an unrepairable mismatch. Null in the normal case.
   * renderExtrasTotal() clears it, so any later change to the plan or the
   * accessories invalidates a pending confirmation automatically. */
  let confirmTotalCents = null;
  let postAddPending = null; // attrs+offer settle in the background after add (2026-07-29 speed fix)
  let postAddBusy = false;   // TRUE only while that background settle is running
  async function toCheckout(withExtras) {
    if (checkingOut) return;
    checkingOut = true;
    // Captured BEFORE anything is awaited: this is the figure on screen at the
    // instant of the tap, and it is what the cart has to match.
    const shownAtTap = confirmTotalCents != null ? confirmTotalCents : displayedTotalCents();
    const err = out('error4');
    err.hidden = true;
    checkoutBtn.disabled = true;
    skipBtn.disabled = true;
    checkoutBtn.textContent = 'Heading to checkout...';
    let failure = null;
    try {
      if (postAddPending) { try { await postAddPending; } catch (_) {} postAddPending = null; }
      if (withExtras) {
        const picked = $$('#aw-gf-step4 [data-gf-extra]').filter((b) => b.checked).map((b) => Number(b.dataset.variant));
        if (picked.length) {
          const cur = await AWCART.read({ fresh: true });
          const have = cur.items.map((i) => i.variant_id);
          const items = picked.filter((v) => have.indexOf(v) === -1).map((v) => ({ id: v, quantity: 1 }));
          if (items.length) {
            const r = await AWCART.addItems(items, cur);
            if (!r.ok) { failure = r; throw new Error('extras add failed ' + r.status); }
          }
        }
      }
      /* ---- THE GUARD. Nothing below may run on an unverified total. ---- */
      checkoutBtn.textContent = 'Checking your total...';
      const v = await verifyCheckoutTotal(shownAtTap);
      try { window.__awCheckoutGuard = v; } catch (_) {} // diagnostic only, no PII
      if (!v.ok) {
        /* 2026-08-09 backstop. A mismatch the repair pass cannot close used to
         * DEAD-END the customer: every subsequent tap failed the same three
         * attempts and the page offered no way forward. Brian hit exactly that
         * on his own phone and the only remedy was clearing cookies, which no
         * real customer will ever do - they simply leave, and we never hear
         * about it. So when the cart IS readable we now RE-SYNC rather than
         * refuse: repaint the summary with the cart's real figure, say plainly
         * that it changed, and let one more deliberate tap go through at that
         * number. The guarantee this guard exists for is intact - the customer
         * is shown a figure read from their own cart moments earlier, the
         * confirming tap RE-VERIFIES it, and any change to the plan or the
         * accessories clears the pending confirmation - so nobody can be sent
         * to a checkout charging more than the page last showed them. */
        if (v.reason === 'mismatch' && v.got != null) {
          const box = $('[data-awz-xtot]');
          const totEl = box && box.querySelector('[data-out="x-total"]');
          if (totEl) totEl.textContent = usd(v.got);
          confirmTotalCents = v.got; // set AFTER the paint: renderExtrasTotal clears it
          err.hidden = false;
          err.textContent = 'Your total is ' + usd(v.got)
            + '. Nothing has been charged yet - tap Check out again to continue at that price.';
        } else {
          err.hidden = false;
          err.textContent = AWCART.message({ transient: true }, 'Check out');
        }
        checkoutBtn.disabled = false;
        skipBtn.disabled = false;
        checkoutBtn.textContent = 'Check out';
        checkingOut = false;
        return; // still never navigates on an unverified total
      }
      markDot(5);
      announce('Step ' + DISPLAY_STEP[5] + ' of ' + DISPLAY_TOTAL + ': Checkout');
      window.location.href = '/checkout';
    } catch (e) {
      err.hidden = false;
      if (!failure) failure = { transient: !!(e && e.transient), status: (e && e.status) || 0 };
      err.textContent = AWCART.message(failure, 'Check out');
      checkoutBtn.disabled = false;
      skipBtn.disabled = false;
      checkoutBtn.textContent = 'Check out';
      checkingOut = false;
    }
  }
  if (checkoutBtn) checkoutBtn.addEventListener('click', () => toCheckout(true));
  if (skipBtn) skipBtn.addEventListener('click', () => toCheckout(false));

  // --- Boot ---
  const resumed = restore();
  state.eligibility = readEligibility();
  // Restore a More-options plan only if it still exists; never boot planless.
  if (state.planOv && state.planId && D.plans.some((p) => p.id === state.planId)) setWeeks(state.weeks, state.planId);
  else setWeeks(state.weeks || 4);
  if (!state.planId && D.plans.length) setWeeks(4);
  applyCadenceUI();
  let startStep = resumed ? state.step : 3; // every visitor lands on the plan card (Task 6)
  // Task 9 deleted step 2; 2026-08-08 deleted step 1. A stale sessionStorage
  // resume pointing at either must land on the plan card, never on a dead panel.
  // A visitor with no verdict also starts here now: the card renders its own
  // "Enter your ZIP to order" state and the hero gate stays expanded above it.
  if (startStep < 3) startStep = 3;
  goTo(startStep, { boot: true });
  // Extras cards are server-rendered unchecked. A returning/resumed visitor whose
  // cart already holds an accessory must see it as "Added", and one whose cart
  // lost it must not. One cart read, after the first paint. Non-blocking.
  syncExtrasFromCart();

  /* Cart edits made OUTSIDE the wizard (2026-08-07). Brian, mobile: removing a
   * line in the side cart left the page still showing it.
   *
   * MEASURED before this fix, 393x852: removing the Wood Counter Stand in the
   * drawer took /cart.js to 9 items / $236.00 and the drawer subtotal to $236,
   * while the extras card still read "Added" and the page's own Order total
   * still read $280. Nothing on this page had ever listened for a cart change it
   * did not make itself. The consequence was not cosmetic: toCheckout() adds
   * every CHECKED extra that is missing from the cart, so the next tap of Check
   * out would have PUT THE STAND BACK and charged $280 for the thing the
   * customer had just removed.
   *
   * theme.js dispatches nothing of its own, but every cart write on this store
   * ends in a 'cart:build' (ours coalesced through AWCART.announce, the theme's
   * and the apps' direct), so that is the signal to re-read.
   *
   * Repaint only. syncExtrasFromCart() reads through the shared transport and
   * never writes, so this cannot loop. Two guards keep it honest:
   *  - suppressed while one of OUR writes is in flight (doAdd re-SETS the order,
   *    zero then re-add, and a repaint taken mid-sequence would read a cart that
   *    is deliberately half-built),
   *  - and skipped when we already synced within the last 1.2s, which is what
   *    stops our own writes paying for a second cart read they do not need.
   *
   * The plan half: if the water line is gone from the cart there IS no order, so
   * step 4's "Order total" would be quoting a cart that does not exist. Send the
   * visitor back to the plan card, where the same figures are honestly labelled
   * a recommendation and the CTA rebuilds the order. Nothing is written here. */
  let extSyncTimer = null;
  function onExternalCartChange() {
    if (adding || xBusy || postAddBusy) return;
    if (Date.now() - lastExtrasSyncAt < 1200) return;
    clearTimeout(extSyncTimer);
    extSyncTimer = setTimeout(async function () {
      if (adding || xBusy || postAddBusy) return;
      const c = await syncExtrasFromCart();
      if (!c) return; // unreadable cart: leave the page exactly as it was
      if (state.step !== 4) return;
      const waterIds = Object.keys(D.regions).map((k) => String(D.regions[k].id));
      const hasWater = (c.items || []).some((i) => waterIds.indexOf(String(i.variant_id)) > -1);
      if (!hasWater) goTo(3);
    }, 600);
  }
  /* BOTH events, and 'cart:updated' is the load-bearing one. Measured 2026-08-07:
   * theme.js's quantityChanged() - the handler behind every +/- and every jugs
   * select in the side cart - ends with `this.buildCart()` and
   * `document.dispatchEvent(new CustomEvent('cart:updated', ...))`. It never
   * dispatches 'cart:build'; that event is what OTHER code fires AT theme.js.
   * A first version of this listener bound 'cart:build' alone and sat silent
   * through eight consecutive drawer removals, with the extras card still
   * reading "Added" over a cart that no longer held the stand. 'cart:build' is
   * kept as well, for writes made by the installed apps. */
  document.addEventListener('cart:updated', onExternalCartChange);
  document.addEventListener('cart:build', onExternalCartChange);
}



// 2026-08-05 journey pass: step-3 sub-panel navigation. Task 9 deleted the
// legacy water/setup/review sub-panels, so only plan and adjust remain; any
// stale sessionStorage aw_gf_sub value (e.g. 'setup') falls through the TITLES
// guard below and lands on the plan card. Deliberately outside the module: it
// only shows/hides panels and swaps the step heading; all pricing and cart
// state logic stays in the module above.
(function () {
  // 2026-08-18 (Brian): 4-jug minimum framing - the card chooses, the chart recommends.
  var TITLES = { plan: 'Choose your plan', adjust: 'Adjust your plan' };
  window.awGfShowSub = function (key, focus) {
    if (!key) { try { key = sessionStorage.getItem('aw_gf_sub') || 'plan'; } catch (_) { key = 'plan'; } }
    if (!TITLES[key]) key = 'plan';
    var subs = document.querySelectorAll('#aw-gf-step3 .aw-gf-sub');
    if (!subs.length) return;
    subs.forEach(function (p) { p.hidden = p.getAttribute('data-gf-sub') !== key; });
    var h = document.getElementById('aw-gf-h3');
    if (h) h.textContent = TITLES[key];
    try { sessionStorage.setItem('aw_gf_sub', key); } catch (_) {}
    if (focus && h) h.focus();
    // Task 10 (2026-08-05): `zone` added; `step`/`sub` unchanged. Gated on
    // `focus`, which only the explicit [data-gf-subnext]/[data-gf-subback]
    // click handlers pass - a boot restore and goTo()'s own plan reset both
    // call this with focus falsy, and chip changes never call it at all, so
    // one click = one event and a hidden panel never emits.
    if (focus) { try { if (typeof window.awTrackGate === 'function') window.awTrackGate('gf_step_view', { gate: 'guided_flow', step: 3, sub: key, zone: gfZone(3, key) }); } catch (_) {} }
  };
  // Boot restore: init() (top of file) has already run by the time this IIFE is
  // defined, so its goTo(boot) call could not reach awGfShowSub. If step 3 is the
  // panel on screen, restore the visitor's stored sub-panel now. No focus move -
  // a boot restore must not steal the caret.
  var step3 = document.getElementById('aw-gf-step3');
  if (step3 && !step3.hidden) window.awGfShowSub(null, false);

  /* Deposit explainer dialog behaviour (2026-08-07, pre-launch audit IMPORTANT-2).
   *
   * THE DEFECT. #awz-deposit carries role="dialog" aria-modal="true" and covers
   * the whole viewport, but it behaved like nothing of the kind: Escape did not
   * close it, focus was never moved into it (activeElement stayed on the H2
   * behind), and Tab walked straight out to the site nav and the testimonials
   * slider underneath, all of which are obscured. Announcing aria-modal while
   * leaving focus outside is worse than not announcing it - a screen reader user
   * is told the rest of the page is inert and then tabbed straight into it.
   *
   * The four required behaviours, and nothing else: focus in on open, focus
   * contained while open, focus returned to the trigger on close, Escape closes.
   * Visual design is untouched. Click-outside-to-close is deliberately NOT added:
   * the overlay fills the viewport, so an outside click is indistinguishable from
   * a mis-tap on a phone, and the X and "Got it" both already work.
   *
   * GENERALISED 2026-08-07 for the glass-dispenser photo dialog. The page now
   * has two dialogs (#awz-deposit and #awz-disp-photo) built from the same
   * .aw-widget__deposit-note markup. Rather than stand up a second mechanism
   * beside an audited one, depEl() stopped being an id lookup and became "the
   * dialog that is currently open" - so the Escape handler, the Tab cycle, the
   * focus-in and the focus-return below are ONE implementation serving both,
   * and any dialog added later inherits all four behaviours by using the same
   * class. depShow() takes its element from the opener instead of finding it. */
  var depTrigger = null;

  // The open dialog, whichever it is. Only one can be open at a time (depShow
  // closes any other first), so this is unambiguous.
  function depEl() { return document.querySelector('.aw-widget__deposit-note.is-open'); }
  function depOpen(el) { return !!el && el.classList.contains('is-open'); }

  function depFocusables(el) {
    var sel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(el.querySelectorAll(sel), function (n) {
      return !n.hasAttribute('hidden') && n.offsetWidth + n.offsetHeight > 0;
    });
  }

  function depShow(el, trigger) {
    if (!el || depOpen(el)) return;
    // Never leave two overlays stacked: close whatever else is open first, but
    // WITHOUT restoring focus to its trigger, since focus is about to move into
    // the new dialog anyway.
    var other = depEl();
    if (other && other !== el) { other.classList.remove('is-open'); depTrigger = null; }
    /* Take the trigger from the CLICK, not from document.activeElement. Safari
     * and WebKit do not focus a link on mousedown, so activeElement here is
     * <body> for the ordinary mouse path - restoring to that on close puts the
     * customer back at the top of the document. Measured, not assumed: the first
     * pass of this fix did read activeElement, and the run came back
     * focusIsTrigger:false / focus:"BODY.template-product". */
    depTrigger = (trigger && typeof trigger.focus === 'function') ? trigger
      : (document.activeElement && document.activeElement !== document.body ? document.activeElement : null);
    el.classList.add('is-open');
    // Focus the dialog itself rather than its close button: the accessible name
    // (aria-label "Why is there a refundable deposit?") is announced on entry and
    // the first Tab lands on the X, which is the expected order.
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); }
  }

  function depHide() {
    var el = depEl();
    if (!el || !depOpen(el)) return;
    el.classList.remove('is-open');
    // Return focus to whatever opened it, so a keyboard user resumes where they
    // were instead of at the top of the document.
    var back = depTrigger;
    depTrigger = null;
    if (back && document.contains(back) && typeof back.focus === 'function') {
      try { back.focus({ preventScroll: true }); } catch (_) { back.focus(); }
    }
  }

  document.addEventListener('keydown', function (e) {
    var el = depEl();
    if (!depOpen(el)) return;
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); depHide(); return; }
    if (e.key !== 'Tab') return;
    var f = depFocusables(el);
    if (!f.length) { e.preventDefault(); el.focus(); return; }
    /* Drive the WHOLE cycle rather than only wrapping at the ends. Two reasons,
     * both measured: WebKit on macOS does not move Tab focus onto buttons at all
     * under its default full-keyboard-access setting, so a Tab left to the engine
     * blurs to <body> and escapes the dialog; and focus starts on the dialog
     * container itself, which is in no engine's tab order. Managing every Tab
     * makes containment engine-independent. The dialog holds two controls (the X
     * and "Got it"), so this is a two-stop cycle. */
    e.preventDefault();
    var i = f.indexOf(document.activeElement);
    var n;
    if (i === -1) n = e.shiftKey ? f.length - 1 : 0; // focus was on the dialog, or outside
    else n = e.shiftKey ? (i - 1 + f.length) % f.length : (i + 1) % f.length;
    f[n].focus();
  });

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var next = e.target.closest('[data-gf-subnext]');
    if (next) { window.awGfShowSub(next.getAttribute('data-gf-subnext'), true); return; }
    var back = e.target.closest('[data-gf-subback]');
    if (back) { window.awGfShowSub(back.getAttribute('data-gf-subback'), true); return; }
    // Task 9: the plan card's deposit link opens the rebuilt explainer
    // (#awz-deposit, mock screen 6 pattern). preventDefault so the #awz-deposit
    // href never scroll-jumps; the shared [data-tip-close] branch below closes it.
    var dep = e.target.closest('a[href="#awz-deposit"]');
    if (dep) {
      e.preventDefault();
      depShow(document.getElementById('awz-deposit'), dep);
      return;
    }
    /* Generic dialog opener (2026-08-07). [data-aw-dialog="<id>"] opens that
     * dialog through the same audited path as the deposit explainer. Used by the
     * "Glass dispenser" line-item button in both line-item tables. The trigger
     * element is passed through so focus returns to the exact button that was
     * clicked, not to whichever table happened to be on screen. */
    var dlg = e.target.closest('[data-aw-dialog]');
    if (dlg) {
      e.preventDefault();
      depShow(document.getElementById(dlg.getAttribute('data-aw-dialog')), dlg);
      return;
    }
    var got = e.target.closest('[data-tip-close]');
    if (got) depHide();
  });
})();
